import type { Messages } from './fr';

/** Arabic has six plural forms; French keys only define `_one` / `_other`. */
type ArabicPlurals = { [k: `${string}_${'zero' | 'two' | 'few' | 'many'}`]: string };
type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends string ? string : DeepPartial<T[K]> & ArabicPlurals;
};

/** Arabic. Missing keys fall back to French key by key (i18next fallbackLng). */
export const ar: DeepPartial<Messages> = {
  app: { name: 'الكوابل — التسيير', short: 'الكوابل', tagline: 'تسيير الإنتاج' },
  errors: {
    title: 'عذرًا، حدث خطأ',
    body: 'واجهت الصفحة خطأ. أعد التحميل للمتابعة؛ البيانات المسجلة لم تُفقد.',
    reload: 'إعادة تحميل الصفحة',
    notFoundTitle: 'الصفحة غير موجودة',
    notFoundBody: 'هذا العنوان غير موجود (أو لم يعد موجودًا). عُد إلى الصفحة الرئيسية.',
    home: 'العودة إلى الرئيسية',
  },
  prod: {
    client: {
      forName: 'لـ {{name}}',
      title: 'الزبون',
      all: 'كل الزبائن',
      filter: 'تصفية حسب الزبون',
      stock: 'للمخزون',
      workshop: 'أمر الورشة (مخزون)',
      for: 'لطلبية الزبون',
      forHint: 'تُحجز له البكرات. فارغ = للمخزون.',
      card: 'للزبون',
      ordered: 'المطلوب: {{kg}}',
      wanted: 'مطلوبة في {{date}}',
      attach: 'ربط بطلبية',
      change: 'تغيير الطلبية',
      detach: 'فك الربط (للمخزون)',
      attachTitle: 'ربط {{code}} بطلبية زبون',
      attachHint:
        'البكرات المصنوعة من الآن في المرحلة الأخيرة تُحجز لهذه الطلبية. الطلبيات المعروضة: نفس القطر، غير قيد الإنتاج بعد.',
      none: 'لا توجد طلبية بهذا القطر تنتظر الإنتاج.',
      detachConfirm: 'فك ربط أمر التصنيع بالطلبية؟ البكرات المحجوزة سابقًا تبقى محجوزة.',
      forClient: 'لـ {{name}} · {{code}}',
    },
    grossTare: 'إجمالي {{gross}} − تارة {{tare}}',
    cycle: 'دورة أمر التصنيع',
    stepNext: 'المرحلة التالية',
    stepTotals_zero: 'دخل {{in}} ← خرج {{out}} · هدر {{scrap}} · {{count}} إدخال',
    stepTotals_one: 'دخل {{in}} ← خرج {{out}} · هدر {{scrap}} · إدخال واحد',
    stepTotals_two: 'دخل {{in}} ← خرج {{out}} · هدر {{scrap}} · إدخالان',
    stepTotals_few: 'دخل {{in}} ← خرج {{out}} · هدر {{scrap}} · {{count}} إدخالات',
    stepTotals_many: 'دخل {{in}} ← خرج {{out}} · هدر {{scrap}} · {{count}} إدخالًا',
    stepTotals_other: 'دخل {{in}} ← خرج {{out}} · هدر {{scrap}} · {{count}} إدخال',
    correctWeight: 'تصحيح الوزن',
    correctTitle: 'تصحيح الوزن — {{code}}',
    correctNow: 'الوزن المسجَّل: {{net}}.',
    correctNowContainer: 'المسجَّل: {{container}} إجمالي {{gross}} − تارة {{tare}} = {{net}} صافٍ.',
    newGross: 'الوزن الإجمالي الصحيح (كغ)',
    newNet: 'الوزن الصافي الصحيح (كغ)',
    newNetIs: 'الصافي الجديد: {{net}}',
    correctReason: 'السبب (مثلًا خطأ في قراءة الميزان)',
    correctAudit:
      'يُسجَّل التصحيح في المخزون (تعديل بهذا السبب) وفي سجل التدقيق. ممكن ما دام لم يُؤخذ شيء من الدفعة.',
    stockOnlyHint:
      'اختر المنتج (قطره) والوزن المراد تصنيعه: تُملأ المرحلة النهائية والخط تلقائيًا.',
    moreOptions: 'خيارات أكثر (الأولوية، التواريخ، الملاحظات)',
    lessOptions: 'خيارات أقل',
    next: {
      draft: 'مسودة: تحقق من الهدف، ثم «إطلاق» ليظهر الأمر على لوحة الورشة.',
      released:
        'مُطلَق: تختار الورشة هذا الأمر في «الإنتاج» على اللوحة وتسجّل كل مرحلة. تظهر حصيلة النحاس هنا منذ أول إدخال.',
      cancelled: 'أمر ملغى، دون إنتاج.',
    },
    woKind: 'نوع أمر التصنيع',
    kindFinished: 'منتج نهائي (صنف ووحدات)',
    kindStock: 'للمخزون (قيد التصنيع حتى مرحلة)',
    forStock: 'للمخزون',
    until: 'حتى {{stage}}',
    endStage: 'مرحلة النهاية',
    targetKg: 'الهدف (كغ)',
    workOrders: 'أوامر التصنيع',
    newWo: 'أمر تصنيع جديد',
    noSku:
      'لا يوجد صنف للإنتاج. الصنف = منتج في نوع تعبئة: أنشئ أولًا نوع التعبئة (مثلًا لفة 100 م)، ثم الصنف.',
    trace: 'التتبّع',
    filters: { open: 'الجارية / المطلقة', draft: 'المسودات', done: 'المنتهية', all: 'الكل' },
    code: 'أمر التصنيع',
    product: 'المنتج',
    sku: 'الصنف',
    line: 'الخط',
    target: 'الهدف',
    produced: 'المُنتَج',
    targetUnits: 'الوحدات المطلوب إنتاجها',
    targetLengthM: 'الطول الإجمالي (م)',
    targetLengthHint: 'إلزامي لصنف بالقطع',
    plannedStart: 'البداية المتوقعة',
    plannedEnd: 'النهاية المتوقعة',
    priority: 'الأولوية (0–9)',
    notes: 'ملاحظات',
    status: 'الحالة',
    currentStage: 'المرحلة الجارية',
    actions: {
      release: 'إطلاق',
      unrelease: 'إرجاع إلى مسودة',
      complete: 'إنهاء',
      cancel: 'إلغاء أمر التصنيع',
      edit: 'تعديل',
      confirmComplete: 'إنهاء أمر التصنيع هذا؟ تبقى الدفعات قيد التصنيع المتبقية في المخزون.',
      confirmCancel: 'إلغاء أمر التصنيع هذا؟',
    },
    routing: 'مسار التصنيع',
    stageLogs: 'سجل المراحل',
    wipLots: 'دفعات قيد التصنيع',
    finishedUnits: 'الوحدات المنتجة',
    printSelected: 'طباعة الملصقات',
    copper: 'حصيلة النحاس لأمر التصنيع',
    copperIn: 'النحاس الداخل',
    copperFg: 'المنتجات التامة (نظري)',
    copperScrap: 'الهدر (نحاس)',
    copperWip: 'تغيّر قيد التصنيع',
    copperSold: 'المبيع قيد التصنيع',
    variance: 'الفارق',
    flagged: 'فارق يتجاوز الحد',
    operatorCol: 'العامل',
    machine: 'الآلة',
    inputs: 'المدخلات',
    output: 'المخرجات',
    scrap: 'الهدر',
    date: 'التاريخ',
    remaining: 'المتبقي',
    qc: 'الجودة',
    stock: 'المخزون',
    lengthM: 'الطول',
    netKg: 'الوزن الصافي',
    none: 'لا شيء',
    traceIntro: 'امسح أو أدخل رمز دفعة أو رقمًا تسلسليًا.',
    backward: 'المصدر (المنبع)',
    forward: 'الوجهة (المصب)',
    search: 'بحث',
    truncated: '… {{count}} أخرى',
    repeated: 'معروض سابقًا',
    wipStatus: { AVAILABLE: 'متاح', CONSUMED: 'مستهلك', ON_HOLD: 'موقوف', SCRAPPED: 'مُهدَر' },
  },
  situation: {
    title: 'وضعية الزبائن',
    intro:
      'من سدّد ومن عليه دين: الفواتير المصادق عليها والتسديدات والإشعارات الدائنة والباقي والتأخر. انقر على زبون لكشف حسابه.',
    owed: 'الباقي للدفع',
    ofInvoiced: 'من {{amount}} مفوترة',
    late: 'منها متأخر',
    customersCount_zero: 'لا زبون متأخر',
    customersCount_one: 'زبون واحد متأخر',
    customersCount_two: 'زبونان متأخران',
    customersCount_few: '{{count}} زبائن متأخرين',
    customersCount_many: '{{count}} زبونًا متأخرًا',
    customersCount_other: '{{count}} زبون متأخر',
    debtHint: 'عليهم دين دون تأخر',
    noInvoice_zero: 'لا أحد دون فاتورة',
    noInvoice_one: 'زبون واحد دون فاتورة',
    noInvoice_two: 'زبونان دون فاتورة',
    noInvoice_few: '{{count}} زبائن دون فاتورة',
    noInvoice_many: '{{count}} زبونًا دون فاتورة',
    noInvoice_other: '{{count}} زبون دون فاتورة',
    filter: 'الوضعية',
    all: 'الكل',
    search: 'الزبون أو الرمز أو المدينة…',
    empty: 'لا يوجد زبون لهذا الاختيار.',
    situation: 'الوضعية',
    invoiced: 'المفوتر شامل الرسوم',
    settled: 'المسدّد',
    balance: 'الباقي للدفع',
    overdue: 'متأخر',
    openInvoices: 'فواتير مفتوحة',
    lastPayment: 'آخر تسديد',
    daysLate_zero: 'دون تأخر',
    daysLate_one: 'يوم تأخر واحد',
    daysLate_two: 'يوما تأخر',
    daysLate_few: '{{count}} أيام تأخر',
    daysLate_many: '{{count}} يومًا من التأخر',
    daysLate_other: '{{count}} يوم تأخر',
    overLimit: 'تجاوز السقف ({{limit}})',
    status: { NONE: 'دون فاتورة', PAID: 'مسدَّد', DEBT: 'عليه دين', OVERDUE: 'متأخر' },
  },
  sales: {
    inv: {
      issued: 'صدرت في {{date}}',
      due: 'تستحق في {{date}}',
      printPdf: 'طباعة / PDF',
      recordPayment: 'تسجيل دفعة',
      docTitle: 'فاتورة',
      draftTitle: 'فاتورة (مسودة)',
      date: 'التاريخ',
      dueLabel: 'الاستحقاق',
      order: 'الطلبية',
      dn: 'وصل التسليم',
      billedTo: 'إلى',
      designation: 'التعيين',
      customerMissing: 'الزبون {{name}}: ينقص {{list}} — إلزامي لاعتماد الفاتورة.',
      completeCustomer: 'إكمال بطاقة الزبون',
      addressWord: 'العنوان',
      draftHint:
        'مسودة: يمكنك بعد تغيير سعر الوحدة والضريبة لكل سطر (السعر يتغير)، ثم الحفظ. بعد المصادقة لا تتغير الفاتورة (التصحيح عبر إشعار دائن).',
      diameter: 'القطر',
      qty: 'الكمية',
      priceHt: 'السعر دون رسوم',
      amountHt: 'المبلغ دون رسوم',
      totalWeight: 'الوزن الإجمالي المسلَّم',
      vatLine: 'الرسم على القيمة المضافة {{rate}} %',
      stamp: 'حق الطابع',
      stampNone: 'لا دفع نقدي',
      netToPay: 'الصافي للدفع',
      payBy: 'الدفع: {{method}}',
      validatedOn: 'صودق عليها في {{date}} · لا يمكن تعديلها',
      draftNote: 'مسودة: قابلة للتعديل حتى المصادقة',
      payments: 'الدفعات',
      add: 'إضافة',
      remaining: 'المتبقي للدفع',
      dueIn: 'تستحق بعد {{days}} يوم',
      dueToday: 'تستحق اليوم',
      lateBy: 'متأخرة {{days}} يوم',
      paidPct: 'مدفوع {{paid}} · {{pct}} %',
      of: 'من {{total}}',
      noPayment: 'لا دفعات مسجلة',
      linked: 'الوثائق المرتبطة',
      orderDoc: 'طلبية · {{date}}',
      dnDoc_zero: 'وصل تسليم · {{date}} · {{count}} دفعة',
      dnDoc_one: 'وصل تسليم · {{date}} · دفعة واحدة',
      dnDoc_two: 'وصل تسليم · {{date}} · دفعتان',
      dnDoc_few: 'وصل تسليم · {{date}} · {{count}} دفعات',
      dnDoc_many: 'وصل تسليم · {{date}} · {{count}} دفعة',
      dnDoc_other: 'وصل تسليم · {{date}} · {{count}} دفعة',
      creditNotes: 'الأفوار',
      noCredit: 'لا يوجد أفوار',
      noCreditHint:
        'الفاتورة المصادق عليها لا تُحذف. لتصحيحها، «إلغاء الفاتورة» يصدر أفوارًا ويمكنه إرجاع السلك إلى المخزون.',
      partialCredit: 'أفوار جزئي أو إرجاع الزبون',
      history: 'السجل',
      hDraft: 'أُنشئت المسودة',
      hDraftFrom: 'أُنشئت المسودة من {{dn}}',
      hValidated: 'صودق على الفاتورة',
      hPayment: 'سُجلت دفعة: {{method}}',
      hCredit: 'أفوار {{number}}',
    },
    rev: {
      title: 'إلغاء الفاتورة {{number}}',
      sub: '{{customer}} · {{ttc}} مع الرسوم',
      intro: 'الفاتورة لا تُحذف. يصدر أفوار لكل ما لم يُعوَّض بعد، ويسدّد الفاتورة.',
      total: 'أفوار كلي',
      partial: 'أفوار جزئي',
      what: 'ماذا يحدث للسلك المسلَّم؟',
      stock: 'إرجاع إلى المخزون في دفعاته',
      stockHint: 'تعود {{kg}} إلى دفعاتها ({{dn}})، بعد وزن للتحقق.',
      scrap: 'إرجاع إلى الخردة',
      scrapHint: 'يعود السلك إلى الصهر كخردة نحاس.',
      none: 'دون إرجاع السلك',
      noneHint: 'أفوار مالي فقط، مثلًا لخطأ في السعر.',
      paid: '{{paid}} مدفوعة تبقى رصيدًا للزبون، أو يمكن ردها من «وضعية الزبون».',
      reason: 'السبب (إلزامي)',
      reasonPh: 'مثال: خطأ في القطر في السطر 2',
      back: 'رجوع',
      confirm: 'إصدار الأفوار والإلغاء',
    },
    returnWire: 'السلك الذي أرجعه الزبون',
    returnWireHint:
      'زِن ما يعود، بكرةً بكرة: يعود إلى المخزون النهائي، أو إلى الخردة إن كان لإعادة الصهر. الوزن المدفوع يتبعه (قابل للتعديل).',
    wireReturned: 'يعود إلى المخزون (أو إلى الخردة) عند المصادقة على الإشعار الدائن.',
    returnTo: 'الوجهة',
    toStock: 'العودة إلى المخزون النهائي',
    toScrap: 'إلى الخردة (لإعادة الصهر)',
    wireLeft: 'ما يزال عند الزبون: {{kg}}',
    returnKg: 'الوزن المُرجَع',
    reverse: 'إلغاء الفاتورة',
    reverseConfirm: 'الإلغاء بإشعار دائن',
    reverseIntro:
      'الفاتورة المصادَق عليها لا تُحذف (ترقيم قانوني). ينشئ الإلغاء إشعارًا دائنًا لكل ما لم يُسترجع بعد، ويُصادَق عليه فورًا: تُسوّى الفاتورة.',
    reverseReturn: 'تعود البضاعة المسلَّمة إلى المخزون (يُرجعها الزبون)',
    byWeight: 'بالوزن (سلك عارٍ حسب القطر)',
    deleteDraft: 'حذف المسودة',
    confirmDeleteDraft: 'حذف هذه المسودة نهائيًا؟ (ليس لها رقم بعد)',
    lockedInvoice: 'فاتورة مصادق عليها: لا تُحذف — لإلغائها أنشئ إشعارًا دائنًا.',
    lockedDn: 'وصل مصادق عليه: لا يُحذف — الإرجاع يمر عبر إشعار دائن على الفاتورة.',
    orderBy: 'الطلب',
    byUnits: 'بالوحدات',
    byKg: 'بالوزن (كغ)',
    productOptional: 'المنتج (اختياري)',
    anyProduct: '— أي منتج (المرحلة + القطر) —',
    cut: 'بالقطع',
    nav: {
      overview: 'نظرة عامة',
      orders: 'الطلبيات',
      deliveries: 'وصولات التسليم',
      invoices: 'الفواتير',
      creditNotes: 'الإشعارات الدائنة',
      proformas: 'الفواتير الشكلية',
      customers: 'الزبائن',
      receivables: 'الديون',
      situation: 'وضعية الزبائن',
    },
    orderStatus: {
      DRAFT: 'مسودة',
      CONFIRMED: 'مؤكدة',
      PARTIALLY_DELIVERED: 'مسلّمة جزئيًا',
      DELIVERED: 'مسلّمة',
      CANCELLED: 'ملغاة',
    },
    docStatus: { DRAFT: 'مسودة', VALIDATED: 'مُعتمد', CANCELLED: 'ملغى' },
    payStatus: { UNPAID: 'غير مدفوعة', PARTIAL: 'مدفوعة جزئيًا', PAID: 'مدفوعة' },
    priceUnit: { PER_METER: 'دج / م', PER_UNIT: 'دج / وحدة', PER_KG: 'دج / كغ' },
    newOrder: 'طلبية جديدة',
    order: 'الطلبية',
    customer: 'الزبون',
    date: 'التاريخ',
    number: 'الرقم',
    lines: 'البنود',
    addLine: 'إضافة بند',
    kind: 'نوع البيع',
    sku: 'الصنف',
    cutSku: 'صنف بالقطع',
    product: 'المنتج',
    stage: 'المرحلة',
    diameter: 'القطر (مم)',
    units: 'الوحدات',
    pieces: 'القطع',
    cutLength: 'طول القطعة (م)',
    qtyKg: 'الكمية (كغ)',
    qtyM: 'الكمية (م)',
    priceUnitLabel: 'السعر لكل',
    unitPrice: 'السعر خارج الرسوم',
    vat: 'الرسم على القيمة المضافة %',
    paymentMethod: 'طريقة الدفع',
    requestedDate: 'تاريخ التسليم المطلوب',
    notes: 'ملاحظات',
    confirm: 'تأكيد الطلبية',
    cancelOrder: 'إلغاء الطلبية',
    confirmCancel: 'إلغاء هذه الطلبية؟ سيتم تحرير الحجوزات.',
    reserve: 'حجز',
    autoReserve: 'حجز تلقائي',
    reserved: 'محجوز',
    delivered: 'مسلَّم',
    release: 'تحرير',
    candidates: 'المخزون المتاح',
    noCandidates: 'لا يوجد مخزون مطابق.',
    reserveKg: 'كغ للحجز',
    deliver: 'تحضير تسليم',
    newDelivery: 'وصل تسليم جديد',
    vehicle: 'المركبة',
    driver: 'السائق',
    address: 'عنوان التسليم',
    customerAddress: 'العنوان (يُطبع على الفواتير)',
    items: 'الدفعات',
    validate: 'اعتماد',
    validateDn: 'اعتماد التسليم (إخراج من المخزون)',
    billDn: 'فوترة هذا التسليم',
    pdf: 'PDF',
    invoice: 'الفاتورة',
    newInvoice: 'فوترة التسليمات',
    uninvoiced: 'تسليمات غير مفوترة',
    createInvoice: 'إنشاء الفاتورة (مسودة)',
    validateInvoice: 'اعتماد الفاتورة (رقم نهائي)',
    confirmValidate:
      'اعتماد؟ تحصل الفاتورة على رقم نهائي ولا يمكن تعديلها بعد ذلك (التصحيح عبر إشعار دائن).',
    cancelDraft: 'التخلي عن المسودة',
    totalHt: 'المجموع خارج الرسوم',
    totalVat: 'الرسم على القيمة المضافة',
    totalTtc: 'المجموع متضمن الرسوم',
    stamp: 'حق الطابع',
    netToPay: 'الصافي للدفع',
    paid: 'المدفوع',
    remaining: 'الباقي للدفع',
    dueDate: 'تاريخ الاستحقاق',
    inWords: 'أوقفت هذه الفاتورة على مبلغ',
    payments: 'التسديدات',
    addPayment: 'تسجيل تسديد',
    amount: 'المبلغ',
    reference: 'المرجع (رقم الشيك، التحويل…)',
    bank: 'البنك',
    creditNote: 'إشعار دائن',
    newCreditNote: 'إنشاء إشعار دائن',
    reason: 'السبب',
    creditQty: 'الكمية الدائنة',
    returnUnits: 'وحدات مُرجعة إلى المخزون',
    returnUnitsHint:
      'حدد الوحدات التي يعيدها الزبون: تُملأ الكمية الدائنة تلقائيًا، وتعود إلى المخزون عند اعتماد الإشعار الدائن في انتظار مراقبة الجودة.',
    onCreditNote: 'موجودة في الإشعار الدائن {{number}}',
    backInStock: 'عادت إلى المخزون',
    returnOnValidate: 'ستعود هذه الوحدات إلى المخزون عند اعتماد الإشعار الدائن.',
    returnedQc: 'عادت إلى المخزون في انتظار مراقبة الجودة: لا يمكن تسليمها إلا بعد مراقبة مطابقة.',
    validateCreditNote: 'اعتماد الإشعار الدائن',
    proforma: 'فاتورة شكلية',
    newProforma: 'فاتورة شكلية جديدة',
    description: 'التعيين',
    qty: 'الكمية',
    validUntil: 'صالحة حتى',
    receivables: 'ديون الزبائن',
    aging: {
      notDue: 'غير مستحقة',
      d30: '1–30 يومًا',
      d60: '31–60 يومًا',
      d90: '61–90 يومًا',
      over90: '> 90 يومًا',
      total: 'المجموع',
    },
    overdue: 'فواتير مستحقة',
    daysLate: 'التأخير (أيام)',
    statement: 'كشف الحساب',
    debit: 'مدين',
    credit: 'دائن',
    balance: 'الرصيد',
    paymentTerms: 'أجل الدفع (أيام)',
    creditLimit: 'سقف الائتمان (دج)',
    code: 'الرمز',
    codeHint: 'CLI-0001… إن تُرك فارغًا',
    available: 'متاح',
    name: 'التسمية الاجتماعية',
    city: 'المدينة',
    wilaya: 'الولاية',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    contact: 'جهة الاتصال',
    legalForm: 'الشكل القانوني',
    articleImposition: 'مادة الإخضاع الضريبي',
    active: 'نشط',
    flowHint: 'من أين تخرج البضاعة لهذا النوع من البيع:',
    qcWarning: '{{count}} وحدة دون مراقبة جودة مطابقة: سيُرفض الاعتماد.',
    toOrder: 'إنشاء الطلبية',
    approvalPending:
      'تجاوز سقف الائتمان أو ديون مستحقة: طُلبت موافقة المالك في {{date}}. ستُؤكَّد الطلبية فور موافقته.',
    approvalRejected: 'رفضها {{name}} في {{date}}.',
    fromProforma: 'من الفاتورة الشكلية {{number}} — راجع البنود المشار إليها ثم احفظ.',
    customerFromProforma: 'زبون الفاتورة الشكلية',
    proformaExpired:
      'انتهت صلاحية الفاتورة الشكلية في {{date}}: تأكد من أن الأسعار لا تزال سارية قبل الحفظ.',
    proformaLine: 'الفاتورة الشكلية: {{description}} — {{qty}} × {{price}} {{unit}}',
    proformaStatus: {
      DRAFT: 'مسودة',
      ISSUED: 'صادرة',
      ACCEPTED: 'مقبولة',
      EXPIRED: 'منتهية الصلاحية',
      CANCELLED: 'ملغاة',
    },
    proformaIssue: {
      NO_SKU: 'بند حر في الفاتورة الشكلية: اختر الصنف المبيع (أو احذف البند).',
      PER_KG: 'السعر بالكيلوغرام في الفاتورة الشكلية: أدخل السعر بالمتر أو بالوحدة.',
      CUT_LENGTH_MISSING: 'قطع بالقطع: حدد طول كل قطعة.',
      UNITS_MISSING: 'نوع تعبئة دون طول اسمي: حدد عدد الوحدات.',
      ROUNDED_UP: 'تم التقريب إلى أقرب وحدة تعبئة كاملة: تحقق من الكمية مع الزبون.',
      FRACTIONAL_UNITS: 'تم تقريب الكمية إلى الوحدة الأعلى.',
      ONE_PIECE: 'قطعة واحدة بالطول المطلوب: قسّمها إذا أراد الزبون عدة قطع.',
    },
  },
  theme: {
    label: 'المظهر',
    light: 'فاتح',
    dark: 'داكن',
    system: 'تلقائي',
    systemHint: 'يتبع إعداد الجهاز',
    cycle: 'المظهر: {{name}}',
  },
  nav: {
    fusion: 'الصهر (3 مم)',
    entry: 'تسجيل الإنتاج',
    yields: 'المردود والخسائر',
    materialsMonthly: 'الخردة و3 مم شهريًا',
    stockOverview: 'نظرة عامة',
    users: 'المستخدمون',
    materialLots: 'المواد والدفعات',
    help: 'مساعدة',
    dashboard: 'لوحة القيادة',
    settings: 'الإعدادات العامة',
    rawMaterials: 'المواد الأولية',
    materialsStock: 'المخزون والدفعات',
    salesDeliveries: 'المبيعات والتسليم',
    reportsSettings: 'التقارير والإعدادات',
    search: 'البحث عن رقم',
    searchPlaceholder: 'رقم دفعة، سلة، بكرة، أمر، فاتورة…',
    notFound: 'غير موجود',
    configuration: 'الإعدادات',
    inventory: 'المخزون',
    production: 'الإنتاج',
    sales: 'المبيعات',
    reports: 'التقارير',
    audit: 'سجل التدقيق',
    operator: 'الورشة',
    owner: 'الإدارة',
    admin: 'الإدارة والإعدادات',
    apps: 'التطبيقات',
    main: 'التنقل الرئيسي',
    breadcrumb: 'مسار التنقل',
    menu: 'القائمة',
    collapse: 'طي القائمة',
    expand: 'توسيع القائمة',
  },
  start: {
    title: 'الخطوات الأولى',
    progress: '{{done}} من {{total}} منجزة',
    intro:
      'ما بقي إعداده، بالترتيب. كل سطر يفتح الشاشة المناسبة؛ تختفي هذه البطاقة عند إنجاز الكل.',
    items: {
      company: { title: 'هوية الشركة', hint: 'NIF، RC، العنوان: تُطبع على الفواتير.' },
      steps: {
        title: 'مراحل التصنيع',
        hint: 'السحب، التلدين… بترتيب العملية (سلك 3 مم يأتي من الصهر).',
      },
      line: { title: 'خط إنتاج', hint: 'سلسلة المراحل التي تنجزها آلاتكم.' },
      product: { title: 'منتج', hint: 'مثلًا سلك عارٍ Ø 1,30: القطر والمراحل المتبعة.' },
      material: { title: 'مادة «خردة النحاس»', hint: 'المواد: خردة النحاس، سلك 3 مم، PVC…' },
      team: {
        title: 'حساب من يسجّل الإنتاج',
        hint: 'حساب «مسؤول» (المكتب) أو «عامل» (الورشة) لكل شخص.',
      },
      receive: { title: 'أول استلام للخردة', hint: 'المالك، من هاتفه: «استلام الخردة».' },
      fusion: { title: 'أول صهر', hint: 'الإنتاج › الصهر (3 مم): تصبح الخردة سلالًا من سلك 3 مم.' },
      production: {
        title: 'أول تسجيل للإنتاج',
        hint: 'الإنتاج › تسجيل الإنتاج: السحب ثم التلدين، في بكرات.',
      },
    },
  },
  ownerOrders: {
    useNewClient: 'استعمال هذا الزبون الجديد',
    saleByWeight: 'بيع بالوزن · السعر للكيلوغرام',
    pickClient: 'اختر الزبون',
    change: 'تغيير',
    searchClient: 'ابحث عن زبون',
    overdueLine_zero: '{{count}} فاتورة متأخرة · المتبقي {{value}}',
    overdueLine_one: 'فاتورة واحدة متأخرة · المتبقي {{value}}',
    overdueLine_two: 'فاتورتان متأخرتان · المتبقي {{value}}',
    overdueLine_few: '{{count}} فواتير متأخرة · المتبقي {{value}}',
    overdueLine_many: '{{count}} فاتورة متأخرة · المتبقي {{value}}',
    overdueLine_other: '{{count}} فاتورة متأخرة · المتبقي {{value}}',
    noOverdueLine: 'لا مستحقات متأخرة',
    diameterMm: 'القطر (مم)',
    freeStock:
      '{{free}} من سلك Ø {{d}} مم متاحة في المخزون ويمكن حجزها. المتبقي للإنتاج: {{rest}}.',
    freeCovers: '{{free}} من سلك Ø {{d}} مم متاحة في المخزون: يمكن تلبية الطلبية من المخزون.',
    noFree: 'لا يوجد سلك Ø {{d}} مم متاح في المخزون: ستذهب {{qty}} إلى الإنتاج.',
    qty: 'الكمية',
    salePrice: 'سعر البيع',
    perKg: 'دج/كغ',
    lineSum: 'Ø {{d}} مم · {{qty}} × {{price}}',
    addDiameter: 'إضافة قطر آخر',
    removeLine: 'حذف هذا السطر',
    wantedDate: 'التسليم المطلوب',
    totalHt: 'المجموع دون رسوم',
    vat: 'الرسم على القيمة المضافة {{rate}} %',
    totalTtc: 'المجموع مع الرسوم',
    create: 'إنشاء الطلبية',
    title: 'طلبيات الزبائن',
    new: 'طلبية جديدة',
    newSub: 'تصل فورًا إلى مسؤولة الإنتاج',
    receive: 'استلام الخردة',
    none: 'لا طلبيات جارية.',
    client: 'الزبون',
    newClient: '+ زبون جديد',
    newClientName: 'اسم الزبون الجديد',
    diameter: 'القطر',
    noProduct: 'لا يوجد قطر: أضف منتجًا (الإعدادات › المنتجات).',
    quantity: 'الكمية والسعر',
    qtyKg: 'الكمية (كغ)',
    price: 'السعر دون رسوم (دج / كغ)',
    total: 'المجموع دون رسوم: {{total}} دج',
    wanted: 'مطلوبة في (اختياري)',
    notes: 'ملاحظة (اختياري)',
    send: 'إرسال الطلبية',
  },
  orders: {
    lead: 'طلبيات الزبائن بالوزن: للإطلاق في الإنتاج، قيد الإنتاج، أو جاهزة للتسليم.',
    newWo: 'أمر تصنيع',
    stat: {
      toProduce: 'للإنتاج',
      inProduction: 'قيد الإنتاج',
      ready: 'جاهزة للتسليم',
      wire3mm: 'سلك 3 مم المتاح',
      wire3mmSub: '≈ {{kg}} مطلوبة',
      count_zero: '{{count}} طلبية',
      count_one: 'طلبية واحدة',
      count_two: 'طلبيتان',
      count_few: '{{count}} طلبيات',
      count_many: '{{count}} طلبية',
      count_other: '{{count}} طلبية',
      inProdSub_zero: '{{count}} طلبية · {{kg}} أُنتجت',
      inProdSub_one: 'طلبية واحدة · {{kg}} أُنتجت',
      inProdSub_two: 'طلبيتان · {{kg}} أُنتجت',
      inProdSub_few: '{{count}} طلبيات · {{kg}} أُنتجت',
      inProdSub_many: '{{count}} طلبية · {{kg}} أُنتجت',
      inProdSub_other: '{{count}} طلبية · {{kg}} أُنتجت',
    },
    tabs: {
      active: 'النشطة',
      TO_PRODUCE: 'للإنتاج',
      IN_PRODUCTION: 'قيد الإنتاج',
      READY: 'جاهزة',
      all: 'الكل',
    },
    diameterAll: 'القطر: الكل',
    sortDue: 'الترتيب: الموعد',
    sortCreated: 'الترتيب: الإنشاء',
    col: {
      order: 'الطلبية',
      client: 'الزبون',
      diameter: 'القطر',
      ordered: 'المطلوب',
      progress: 'المُنتَج والمحجوز',
      due: 'الموعد',
      status: 'الحالة',
      action: 'إجراء',
    },
    created: 'أُنشئت في {{date}}',
    nothing: 'لم يُنتَج شيء',
    coveredBy: 'يغطيها المخزون ({{kg}})',
    freeIn: '{{kg}} متاحة في المخزون',
    due: {
      today: 'اليوم',
      tomorrow: 'غدًا',
      in: 'بعد {{days}} يوم',
      late: 'متأخرة {{days}} يوم',
    },
    reserveStock: 'حجز من المخزون',
    seeWo: 'عرض أمر التصنيع',
    prepareDn: 'تحضير وصل التسليم',
    footer: '{{count}} طلبية · {{kg}} مطلوبة',
    need: {
      title: 'الاحتياج حسب القطر',
      intro: 'المتبقي للتسليم من الطلبيات النشطة، مقارنة بالسلك الجاهز المتاح',
      need: 'الاحتياج',
      free: 'سلك جاهز متاح',
      colFree: 'متاح',
      toMake: 'للإنتاج',
      covered: 'مغطى',
      aria: 'الاحتياج {{need}}، المتاح {{free}}',
      total: 'للإنتاج: {{kg}}',
      wire: 'سلك 3 مم اللازم ≈ {{kg}} (مردود السحب × التلدين {{pct}} %)',
      wireNoYield: 'سلك 3 مم اللازم ≈ {{kg}}',
      available: '{{kg}} متاحة',
      short: 'ينقص {{kg}} من سلك 3 مم',
      none: 'لا احتياج: كل الطلبيات النشطة مغطاة.',
    },
    title: 'طلبيات للإنتاج',
    intro:
      'طلبيات الزبائن التي كتبها المالك. «إطلاق» يبدأ إنتاج الطلبية؛ كل بكرة منتهية تُحجز لها؛ «اعتماد» عندما تكون جاهزة.',
    open: 'الجارية',
    all: 'الكل',
    none: 'لا طلبيات للإنتاج.',
    wanted: 'مطلوبة في {{date}}',
    bobbins_zero: '{{count}} بكرة',
    bobbins_one: 'بكرة واحدة',
    bobbins_two: 'بكرتان',
    bobbins_few: '{{count}} بكرات',
    bobbins_many: '{{count}} بكرة',
    bobbins_other: '{{count}} بكرة',
    start: 'إطلاق الإنتاج',
    record: 'تسجيل',
    validate: 'إنهاء',
    validateConfirm: 'هل انتهى إنتاج هذه الطلبية؟',
    nothingYet: 'لم يُنتَج شيء بعد لهذه الطلبية',
    deliver: 'تحضير التسليم',
    status: {
      TO_PRODUCE: 'للإنتاج',
      IN_PRODUCTION: 'قيد الإنتاج',
      READY: 'جاهزة',
      DELIVERED: 'مسلَّمة',
    },
  },
  stock: {
    lots_zero: '{{count}} دفعة',
    lots_one: 'دفعة واحدة',
    lots_two: 'دفعتان',
    lots_few: '{{count}} دفعات',
    lots_many: '{{count}} دفعة',
    lots_other: '{{count}} دفعة',
    sinceDate: 'منذ {{date}}',
    showLots: 'عرض الدفعات',
    hideLots: 'إخفاء',
    groupCount_zero: '{{groups}} منتجات · {{count}} دفعة',
    groupCount_one: '{{groups}} منتجات · دفعة واحدة',
    groupCount_two: '{{groups}} منتجات · دفعتان',
    groupCount_few: '{{groups}} منتجات · {{count}} دفعات',
    groupCount_many: '{{groups}} منتجات · {{count}} دفعة',
    groupCount_other: '{{groups}} منتجات · {{count}} دفعة',
    lead: 'كل دفعة في حاويتها، بدقة الكيلوغرام.',
    balance: {
      title: 'حصيلة النحاس',
      since: 'منذ {{date}} · كل كيلوغرام متتبَّع من الخردة إلى الزبون',
      period: { today: 'اليوم', week: 'الأسبوع', month: 'الشهر' },
      flow: 'مسار النحاس',
      received: 'مستلمة',
      fusionSub: 'سلك 3 مم · المردود {{pct}} %',
      yield: 'المردود {{pct}} %',
      check: 'فرق للتحقق',
      finished: 'المخزون الجاهز',
      finishedSub: 'منها {{kg}} محجوزة',
      clients: 'الزبائن',
      delivered: 'مسلَّمة',
      where: 'أين يذهب النحاس المستلم ({{kg}})؟',
      whereNote: 'خلال الفترة: المسلَّم للزبائن، الضائع، وما بقي في المصنع',
      inFactory: 'باقٍ في المصنع',
      deliveredLeg: 'مسلَّمة',
      lossesLeg: 'خسائر',
      recycledTitle: 'أُعيد إلى الفرن',
      notLost: 'غير ضائع',
      slagTitle: 'الخبث',
      loss: 'خسارة',
      slagSub: '{{pct}} % من النحاس المصهور',
      scrapTitle: 'هدر مصرَّح به',
      gapTitle: 'فرق غير مبرَّر',
      toExplain: 'خسارة · للتبرير',
      nothing: 'لا شيء خلال الفترة',
    },
    tabs: { all: 'الكل', scrap: 'خردة', wire: 'سلك 3 مم', finished: 'سلك جاهز' },
    statusFilter: {
      all: 'الحالة: الكل',
      free: 'متاحة',
      reserved: 'محجوزة',
      hold: 'موقوفة',
    },
    col: {
      container: 'الحاوية',
      containers: 'الحاويات',
      lot: 'الدفعة',
      lots: 'الدفعات',
      product: 'المنتج',
      status: 'الحالة',
      in: 'دخلت في',
      actions: 'إجراءات',
    },
    reservedFor: 'محجوزة · {{code}}',
    onHold: 'موقوفة',
    more: 'عرض {{count}} أخرى',
    count: '{{shown}} من {{total}} دفعة',
    panel: {
      pick: 'اختر دفعة من القائمة لعرض تفاصيلها وتصحيحها أو إخراجها من المخزون، وتتبعها.',
      lot: 'الدفعة {{code}}',
      close: 'إغلاق التفاصيل',
      gross: 'الوزن القائم',
      tare: 'الفارغ',
      reservedTo: 'محجوزة لـ',
      correct: 'تصحيح الوزن',
      print: 'طباعة الملصق',
      trace: 'التتبع',
      fullTree: 'فتح الشجرة الكاملة',
      received: 'دخلت في {{date}}',
      basket: 'سلة',
      bobbin: 'بكرة',
      other: 'حاوية',
      noContainer: 'دون حاوية',
    },
    remove: 'إخراج من المخزون',
    removeConfirm: 'إخراج من المخزون',
    removeKg: 'الوزن المُخرَج (كغ)',
    removeReason: 'السبب (إلزامي)',
    removeIntro:
      'للنحاس الذي يغادر المخزون خارج البيع (ضياع، عيّنة، خطأ إدخال…). بحد أقصى {{kg}} حرة (المحجوز يبقى). يُسجَّل السبب.',
    reason: {
      loss: 'ضياع / سرقة',
      sample: 'عيّنة',
      error: 'خطأ إدخال',
    },
    intro: 'كل ما في المصنع الآن، بترتيب الدورة: كل دفعة وكل سلة وكل بكرة، بوزنها الصافي.',
    search: 'رقم السلة أو البكرة أو الدفعة',
    empty: 'لا شيء هنا حاليًا.',
    container: 'سلة / بكرة',
    net: 'الصافي',
    grossTare: 'الإجمالي − التارة',
    since: 'منذ',
    days_zero: '{{count}} يوم',
    days_one: 'يوم واحد',
    days_two: 'يومان',
    days_few: '{{count}} أيام',
    days_many: '{{count}} يومًا',
    days_other: '{{count}} يوم',
    reserved: 'محجوز {{kg}}',
    free: 'متاح',
    material: 'المادة',
    origin: 'المصدر',
    madeHere: 'صهر (الورشة)',
    recycled: 'مسترجع (لإعادة الصهر)',
    bought: 'مشترى',
    finished: 'المخزون النهائي (بكرات جاهزة)',
    summaryAndThresholds: 'ملخص حسب المرحلة والقطر، عتبات التنبيه',
  },
  cycle: {
    title: 'دورة الإنتاج',
    intro:
      'من الخردة إلى المخزون، بترتيب الورشة: ما ينتظر في كل مرحلة الآن، وما أنجزته في الفترة. «تسجيل» يفتح شاشة المرحلة.',
    record: 'تسجيل',
    scrap: 'استلام الخردة',
    received: 'المستلَم في الفترة',
    inStock_zero: '{{count}} دفعة',
    inStock_one: '{{count}} دفعة',
    inStock_two: 'دفعتان',
    inStock_few: '{{count}} دفعات',
    inStock_many: '{{count}} دفعة',
    inStock_other: '{{count}} دفعة',
    containers_zero: '{{count}} وعاء ممتلئ',
    containers_one: 'وعاء ممتلئ',
    containers_two: 'وعاءان ممتلئان',
    containers_few: '{{count}} أوعية ممتلئة',
    containers_many: '{{count}} وعاءً ممتلئًا',
    containers_other: '{{count}} وعاء ممتلئ',
    units_zero: '{{count}} وحدة معبأة',
    units_one: 'وحدة معبأة',
    units_two: 'وحدتان معبأتان',
    units_few: '{{count}} وحدات معبأة',
    units_many: '{{count}} وحدة معبأة',
    units_other: '{{count}} وحدة معبأة',
    in: 'الداخل',
    out: 'الخارج',
    losses: 'الضياع',
    slagAndGap: 'الخبث + الفارق',
    nothing: 'لا إدخال في الفترة',
    finished: 'الإدخال إلى المخزون (المخزون النهائي)',
    seeStock: 'عرض المخزون',
    orders: 'طلبيات للتسليم',
    ordersSub: 'مؤكدة، لم تُسلَّم بعد',
    seeOrders: 'عرض الطلبيات',
  },
  pa: {
    period: 'الفترة',
    preset: {
      month: 'هذا الشهر',
      lastMonth: 'الشهر الماضي',
      '7': '7 أيام',
      '30': '30 يومًا',
      custom: 'تواريخ…',
    },
    empty:
      'لا يوجد إنتاج مسجَّل في هذه الفترة. تظهر الأرقام بمجرد أن تسجّل الورشة «سلك 3 مم» أو مرحلة.',
    kpi: {
      material: 'المادة الداخلة في الإنتاج',
      materialSub: 'خردة النحاس، سلك 3 مم المشترى، PVC…',
      lost: 'مجموع الضياع',
      lostSub: '{{pct}} من المادة الداخلة',
      scrap: 'منها هدر مصرَّح به',
      scrapSub: 'مسجَّل مع سببه في الورشة',
      gap: 'منها فارق غير مصرَّح به',
      gapSub: 'وزن ناقص لم يصرّح به أحد',
    },
    stepsTitle: 'الضياع والمردود حسب المرحلة',
    stepsIntro:
      'ما ضاع في كل مرحلة، كنسبة مما دخلها: الهدر المصرَّح به ثم الفارق غير المصرَّح به. الشريط الأطول هو المرحلة التي يجب مراقبتها.',
    scale: 'السلّم: الشريط الكامل يساوي {{pct}} ضياع (أعلى مرحلة).',
    woIntro: 'أوامر التصنيع العشرة الأكثر ضياعًا في الفترة، كنسبة من المادة التي استلمتها.',
    showAll: 'عرض الكل ({{count}} أمر)',
    worstOnly: 'العشرة الأكثر ضياعًا',
    causesTitle: 'الضياع حسب السبب',
    causesIntro: 'الأسباب الأثقل أولًا: عالج أعلى القائمة أولًا.',
    dailyTitle: 'يومًا بيوم',
    dailyIntro: 'المادة المعالَجة كل يوم وما آلت إليه. مرّر المؤشر على عمود.',
    detailTitle: 'التفاصيل',
    by: { wo: 'حسب أمر التصنيع', operator: 'حسب العامل', machine: 'حسب الآلة' },
    part: { out: 'منتَج', scrap: 'هدر مصرَّح به', gap: 'فارق غير مصرَّح به' },
    in: 'الداخل',
    yield: 'المردود',
    lossOf: 'ضياع {{pct}}',
    overflow: 'الخارج > الداخل',
    share: '{{pct}} من الضياع',
    cumulative: 'تراكمي {{pct}} %',
    entries_zero: '{{count}} إدخال',
    entries_one: '{{count}} إدخال',
    entries_two: '{{count}} إدخالان',
    entries_few: '{{count}} إدخالات',
    entries_many: '{{count}} إدخالًا',
    entries_other: '{{count}} إدخال',
    entriesCol: 'الإدخالات',
    materialIn: 'المادة الداخلة',
    lostPct: 'الضائع',
    recycled: 'عودة إلى الخردة',
    noLoss: 'لا ضياع في هذه الفترة.',
    noWo: 'لا أوامر تصنيع في هذه الفترة.',
    noMachine: 'لم تُحدَّد آلة في الإدخالات.',
    howTo: 'كيف تُقرأ هذه الأرقام',
    glossary: {
      in: {
        term: 'الداخل',
        def: 'الوزن المأخوذ من المخزون للمرحلة (دفعات المرحلة السابقة، الخردة، PVC…).',
      },
      out: { term: 'المنتَج (الخارج)', def: 'وزن الدفعة المنتجة، موزونة في نهاية المرحلة.' },
      scrap: { term: 'هدر مصرَّح به', def: 'الوزن الضائع الذي صرّح به العامل مع سببه.' },
      gap: {
        term: 'فارق غير مصرَّح به',
        def: 'الداخل − المنتَج − الهدر المصرَّح به: وزن ناقص لم يفسّره أحد. سالب = الخارج أكثر من الداخل (خطأ وزن أو إدخال).',
      },
      yield: { term: 'المردود', def: 'المنتَج ÷ الداخل. 98 % تعني ضياع 2 كغ لكل 100 كغ معالَجة.' },
      material: {
        term: 'المادة الداخلة في الإنتاج',
        def: 'المادة الأولية المستهلكة فعلًا (سلك 3 مم المصنوع هنا يُحسب مرة واحدة، كخردة).',
      },
    },
  },
  owner: {
    stockBlocks: 'المخزون حسب المنتج',
    reservedKg: '{{kg}} محجوزة',
    copperStock: 'النحاس في المخزون',
    seeStock: 'عرض المخزون',
    kind: { scrap: 'خردة', wire3mm: 'سلك 3 مم', wip: 'قيد التصنيع', finished: 'سلك جاهز' },
    split: 'التوزيع: {{parts}}',
    finishedSplit: 'السلك الجاهز: {{reserved}} محجوزة للطلبيات · {{free}} متاحة',
    producedToday: 'السلك الجاهز المُنتَج',
    fusionToday: '+ {{kg}} سلك 3 مم من الصهر',
    salesToday: 'مبيعات اليوم',
    salesTodaySub_zero: '{{value}} · {{count}} وصل تسليم',
    salesTodaySub_one: '{{value}} · وصل تسليم واحد',
    salesTodaySub_two: '{{value}} · وصلا تسليم',
    salesTodaySub_few: '{{value}} · {{count}} وصولات تسليم',
    salesTodaySub_many: '{{value}} · {{count}} وصل تسليم',
    salesTodaySub_other: '{{value}} · {{count}} وصل تسليم',
    overdueSub_zero: 'منها {{value}} متأخرة · {{count}} فاتورة',
    overdueSub_one: 'منها {{value}} متأخرة · فاتورة واحدة',
    overdueSub_two: 'منها {{value}} متأخرة · فاتورتان',
    overdueSub_few: 'منها {{value}} متأخرة · {{count}} فواتير',
    overdueSub_many: 'منها {{value}} متأخرة · {{count}} فاتورة',
    overdueSub_other: 'منها {{value}} متأخرة · {{count}} فاتورة',
    alertsTitle: 'التنبيهات',
    alertsAria: 'التنبيهات: {{count}}',
    alert: {
      overdue: 'الفاتورة {{number}} متأخرة {{days}} يومًا',
      overdueSub: '{{customer}} · المتبقي {{value}}',
      variance: 'فرق النحاس هذا الشهر: {{pct}} %',
      varianceSub: 'الحد ± {{threshold}} % · {{count}} أمر تصنيع خارج الحد',
      ready: 'الطلبية {{code}} جاهزة للتسليم',
      readySub: '{{customer}} · {{kg}}',
      low: '{{name}}: مخزون منخفض',
      lowSub: '{{qty}} {{unit}} · الحد {{threshold}} {{unit}}',
      rod: 'خردة النحاس: {{days}} يوم إنتاج',
      rodSub: 'بمعدل {{kg}} يوميًا',
    },
    tabs: {
      today: 'اليوم',
      orders: 'الطلبيات',
      stock: 'المخزون',
      more: 'المال والمزيد',
    },
    wipForSale: 'قيد التصنيع القابل للبيع (كغ)',
    unpriced_zero: 'لا توجد دفعات بلا سعر شراء',
    unpriced_one: 'دفعة مادة واحدة تنتظر سعر الشراء',
    unpriced_two: 'دفعتان من المواد تنتظران سعر الشراء',
    unpriced_few: '{{count}} دفعات مواد تنتظر سعر الشراء',
    unpriced_many: '{{count}} دفعة مواد تنتظر سعر الشراء',
    unpriced_other: '{{count}} دفعة مواد تنتظر سعر الشراء',
    title: 'الإدارة',
    updatedAt: 'آخر تحديث {{time}}',
    offline: 'غير متصل — آخر البيانات من {{time}}',
    refresh: 'تحديث',
    today: 'إنتاج اليوم',
    todayDetail: '{{units}} وحدة · {{cu}} نحاس',
    month: 'هذا الشهر: {{m}} · {{units}} وحدة',
    todayKgHint: 'سلك منتهٍ (آخر مرحلة تصنيع)',
    packedToday: 'منها {{units}} وحدة معبأة · {{m}}',
    monthKg: 'هذا الشهر: {{kg}}',
    packedMonth: '{{units}} وحدة معبأة ({{m}})',
    last14: 'إنتاج آخر 14 يومًا (كغ)',
    kgCol: 'كغ',
    chartTable: 'عرض القيم',
    day: 'اليوم',
    metres: 'الأمتار',
    units: 'الوحدات',
    rod: 'خردة النحاس',
    daysCover: '≈ {{days}} يوم إنتاج',
    noConsumption: 'لا استهلاك حديث',
    variance: 'فارق النحاس لهذا الشهر',
    varianceOk: 'ضمن الحد المسموح',
    varianceAlert: 'خارج الحد المسموح',
    flaggedWo: '{{count}} أمر تصنيع خارج الحد',
    orders: 'الطلبيات الجارية',
    ordersValue: '{{value}} خارج الرسوم',
    receivables: 'ديون الزبائن',
    overdue: 'منها مستحقة: {{value}}',
    noOverdue: 'لا توجد ديون مستحقة',
    alerts: 'تنبيهات المخزون',
    below: 'تحت الحد ({{threshold}} {{unit}})',
    stock: 'مخزون المنتجات التامة',
    reserved: '{{count}} محجوزة',
    lateInvoices: 'فواتير متأخرة',
    daysLate: '{{days}} يوم تأخير',
    openOrders: 'آخر الطلبيات',
    activity: 'النشاط الأخير',
    reports: 'التقارير (PDF / Excel)',
    backOffice: 'فتح التسيير الكامل',
    running: 'التصنيع الجاري',
    openFull: 'فتح في التسيير الكامل',
    woUnits: '{{done}} / {{target}} وحدة',
    now: 'جارية',
    customers: 'الزبائن',
    call: 'اتصال بـ {{name}}',
    customerCredit: 'رصيد دائن لصالح الزبون',
    creditLimit: 'سقف الائتمان {{limit}}',
    salesYear_zero: 'مبيعات {{year}}: {{value}} خارج الرسوم (لا فاتورة)',
    salesYear_one: 'مبيعات {{year}}: {{value}} خارج الرسوم (فاتورة واحدة)',
    salesYear_two: 'مبيعات {{year}}: {{value}} خارج الرسوم (فاتورتان)',
    salesYear_few: 'مبيعات {{year}}: {{value}} خارج الرسوم ({{count}} فواتير)',
    salesYear_many: 'مبيعات {{year}}: {{value}} خارج الرسوم ({{count}} فاتورة)',
    salesYear_other: 'مبيعات {{year}}: {{value}} خارج الرسوم ({{count}} فاتورة)',
    openInvoices: 'فواتير قيد التحصيل',
    ordersInProgress: 'الطلبيات الجارية',
    lastPayments: 'آخر التسديدات',
    approval: {
      title_zero: 'للموافقة',
      title_one: 'للموافقة',
      title_two: 'للموافقة (2)',
      title_few: 'للموافقة ({{count}})',
      title_many: 'للموافقة ({{count}})',
      title_other: 'للموافقة ({{count}})',
      one: 'موافقة',
      order: 'الطلبية',
      overdue: 'ديون مستحقة: {{value}}',
      overLimit: 'المستحقات بعد الطلبية {{total}} — السقف {{limit}}',
      balance: 'المستحقات الحالية',
      limit: 'سقف الائتمان',
      after: 'المستحقات مع هذه الطلبية',
      overdueLabel: 'منها مستحقة',
      asked: 'طلبها {{name}} في {{date}}',
      note: 'ملاحظة للمكلف بالمبيعات (اختياري)',
      approve: 'موافقة',
      reject: 'رفض',
      approveHint: 'الموافقة تؤكد الطلبية فورًا.',
      decision: 'القرار',
      decidedBy: 'من طرف {{name}} في {{date}}',
      status: {
        PENDING: 'في الانتظار',
        APPROVED: 'مقبولة',
        REJECTED: 'مرفوضة',
        WITHDRAWN: 'مسحوبة (عُدّلت الطلبية)',
      },
    },
    empty: 'لا شيء يُذكر',
    install: 'تثبيت التطبيق',
    push: {
      title: 'التنبيهات على هذا الهاتف',
      intro:
        'تلقَّ إشعارًا عند نزول مادة أو منتج تحت حد المخزون، وعند تجاوز أمر تصنيع حد فارق النحاس، وعند استحقاق فاتورة.',
      on: 'مفعّلة على هذا الجهاز',
      off: 'غير مفعّلة على هذا الجهاز',
      enable: 'تفعيل التنبيهات',
      disable: 'إيقاف',
      test: 'إرسال تجربة',
      testSent: 'أُرسلت التجربة: يصل الإشعار خلال ثوانٍ.',
      testNone: 'لم يتلقَّ أي جهاز التجربة. أعد تفعيل التنبيهات.',
      denied: 'الإشعارات محظورة لهذا الموقع: اسمح بها في إعدادات المتصفح ثم عد إلى هنا.',
      insecure: 'تتطلب التنبيهات العنوان الآمن (https) للخادم المثبّت، ولا تعمل على عنوان التجربة.',
      iosInstall:
        'على الآيفون: افتح الموقع في Safari، ثم مشاركة › «إضافة إلى الشاشة الرئيسية»، وفعّل التنبيهات من التطبيق المثبّت (iOS 16.4 أو أحدث).',
      unsupported: 'هذا المتصفح لا يدعم الإشعارات.',
      serverOff: 'التنبيهات غير مُعدّة على الخادم.',
      error: 'تعذّر تفعيل التنبيهات. حاول مجددًا.',
    },
  },
  materialsMonthly: {
    title: 'الخردة وسلك 3 مم — التحليل الشهري',
    intro:
      'كل شهر: المخزون في البداية، ما دخل (مشترى، مصنوع في الصهر، مسترجع)، ما خرج (مصهور، مسحوب)، فوارق الجرد والمخزون في النهاية. كل شيء من حركات المخزون.',
    fromMonth: 'من شهر',
    toMonth: 'إلى شهر',
    previous: 'الشهر السابق',
    next: 'الشهر التالي',
    dechet: 'خردة النحاس',
    wire: 'سلك 3 مم',
    fusion: 'صهر الشهر',
    noFusion: 'لا صهر هذا الشهر.',
    opening: 'المخزون في بداية الشهر',
    closing: 'المخزون في نهاية الشهر',
    bought: 'مشترى',
    recoveredFusion: 'مسترجع من الصهر',
    recoveredProduction: 'هدر إنتاج مُرجَع',
    returned: 'إرجاع زبون',
    meltedOut: 'مصهور (الصهر)',
    usedOther: 'أخذته مرحلة',
    made: 'مصنوع في الصهر',
    drawnOut: 'أخذه السحب',
    count: 'فارق الجرد',
    corrections: 'تصحيحات وإخراجات',
    baskets: 'السلال',
    basketsN_zero: '{{count}} سلة',
    basketsN_one: 'سلة واحدة',
    basketsN_two: 'سلتان',
    basketsN_few: '{{count}} سلال',
    basketsN_many: '{{count}} سلة',
    basketsN_other: '{{count}} سلة',
    melted: 'الخردة المصهورة',
    made3mm: 'سلك 3 مم المحصَّل',
    recovered: 'المسترجع (إلى الخردة)',
    slag: 'الخبث',
    gap: 'فرق غير مصرَّح به',
    yield: 'مردود الصهر',
    dechetEnd: 'الخردة في نهاية الشهر',
    wireEnd: 'سلك 3 مم في نهاية الشهر',
    drawn: '3 مم المسحوب في الشهر',
    byMonth: 'شهرًا بشهر (كغ)',
    month: 'الشهر',
    colOpening: 'البداية',
    colIn: 'الداخل',
    colOut: 'الخارج',
    colClosing: 'النهاية',
  },
  reports: {
    copperTitle: 'حصيلة مادة النحاس',
    copperIntro:
      'النحاس الداخل (خردة النحاس المستهلكة) = المنتجات التامة (الوزن النظري) + الهدر + تغيّر قيد التصنيع + المبيعات قيد التصنيع + الفارق.',
    from: 'من',
    to: 'إلى',
    allLines: 'كل الخطوط',
    total: 'مجموع الفترة',
    byLine: 'حسب الخط',
    byWo: 'حسب أمر التصنيع',
    threshold: 'الحد: ± {{pct}} %',
    groupBy: 'التجميع حسب',
    group: {
      day: 'اليوم',
      line: 'الخط',
      product: 'المنتج',
      customer: 'الزبون',
      reason: 'السبب',
      stage: 'المرحلة',
      workOrder: 'أمر التصنيع',
    },
    excel: 'Excel',
    pdf: 'PDF',
    asOfToday: 'الوضعية في تاريخه (دون فترة).',
    tabs: {
      copper: 'حصيلة النحاس',
      production: 'تحليل الإنتاج',
      materials: 'الخردة و3 مم',
      scrap: 'الفضلات',
      stock: 'تقييم المخزون',
      sales: 'المبيعات',
      receivables: 'الديون',
    },
    exportError: 'تعذّر التصدير. حاول مجددًا.',
  },
  home: {
    kpi: {
      scrap: 'الخردة المستلَمة (هذا الشهر)',
      scrapSub: 'في المخزون: {{kg}}',
      copper: 'النحاس المنتَج (الصهر، هذا الشهر)',
      copperSub: 'مردود الصهر {{pct}} %',
      copperNone: 'لا صهر هذا الشهر',
      stock: 'المخزون المتاح (سلك جاهز)',
      stockSub: 'منتهٍ اليوم: {{kg}}',
      orders: 'طلبيات للتسليم',
    },
    cycleLink: 'عرض دورة الإنتاج: كل مرحلة، مخزونها وأرقامها',
    welcome: 'مرحبًا، {{name}}',
    intro: 'نظرة عامة على المصنع: الإنتاج، المخزون، الطلبيات والديون.',
    noAlerts: 'لا توجد تنبيهات مخزون.',
    salesIntro: 'من أين تخرج البضاعة لكل نوع بيع، والمخزون المتوفر في الوقت الحقيقي.',
    phase1: 'الأساس التقني جاهز. شاشات العمل ستأتي في المراحل القادمة.',
  },
  common: {
    close: 'إغلاق',
    pager: {
      label: 'ترقيم الصفحات',
      range: '{{from}}–{{to}} من {{total}}',
      page: 'الصفحة {{page}} / {{pages}}',
      prev: 'السابق',
      next: 'التالي',
      goTo: 'الصفحة {{page}}',
    },
    loading: 'جارٍ التحميل…',
    language: 'اللغة',
    comingSoon: 'قريبًا',
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    saved: 'تم الحفظ',
    cancel: 'إلغاء',
    back: 'رجوع',
    search: 'بحث…',
    choose: '— اختر —',
    none: '— لا شيء —',
    empty: 'لا توجد عناصر',
    inactive: 'غير نشط',
  },
  cfg: {
    numbering: {
      title: 'ترقيم الوثائق',
      intro:
        'الفواتير ووصولات التسليم والأفوار والفواتير الأولية والطلبيات. يعود الرقم {N} إلى 1 كل سنة.',
      pattern: 'نموذج الرقم',
      digits: 'عدد أرقام الرقم',
      insert: 'إضافة:',
      tokens: '{PREFIX} = بادئة الوثيقة · {YEAR} = 2026 · {YY} = 26 · {N} = الرقم',
      invoice: 'الفاتورة التالية (مثال)',
      deliveryNote: 'وصل التسليم (مثال)',
      creditNote: 'الأفوار (مثال)',
      legal:
        'يُطبَّق على الوثائق القادمة. الأرقام المسندة سابقًا لا تتغير (ترقيم قانوني دون فجوات).',
    },
    group: { plant: 'المصنع', purchasing: 'المشتريات', catalog: 'الكتالوج', company: 'الشركة' },
    lines: 'خطوط الإنتاج',
    machines: 'الآلات',
    stages: 'المراحل',
    scrapReasons: 'أسباب الهدر',
    wireGauges: 'أقطار السلك',
    containers: 'الأوعية (سلال، بكرات)',
    materials: 'المواد',
    suppliers: 'الموردون',
    supplierCodeHint: 'FRS-0001… إذا تُرك فارغًا',
    families: 'عائلات الكابلات',
    products: 'الأسلاك (واحد لكل قطر)',
    packaging: 'أنواع التعبئة',
    skus: 'الأصناف',
    company: 'الشركة',
    tax: 'الضرائب والترقيم',
    paymentMethods: 'طرق الدفع',
    packagingKinds: 'أنواع التعبئة',
    materialTypes: 'أنواع المواد',
    cash: 'نقدًا',
    showInactive: 'إظهار غير النشط',
    assets: {
      column: 'البوابة',
      title: 'الصورة والبطاقة التقنية',
      intro:
        'تظهر للمشترين في البوابة (الكتالوج وصفحة المنتج). صورة وبطاقة واحدة لكل منتج؛ اختيار ملف جديد يعوّض القديم.',
      photo: 'الصورة',
      datasheet: 'البطاقة التقنية (PDF)',
      none: 'لا شيء',
      photoHint: 'JPEG أو PNG أو WebP. تُصغَّر تلقائيًا (1200 بكسل) قبل الإرسال.',
      datasheetHint: 'PDF، بحد أقصى 10 ميغابايت.',
      choosePhoto: 'اختيار صورة',
      choosePdf: 'اختيار ملف PDF',
      replace: 'تعويض',
      remove: 'إزالة',
    },
    confirmDelete: 'حذف هذا العنصر نهائيًا؟',
    inUse: 'العنصر مستخدم في بيانات أخرى: قم بتعطيله بدلًا من ذلك.',
    stampBrackets: 'حق الطابع (الدفع نقدًا) — الشرائح',
    addBracket: 'إضافة شريحة',
    addSupply: 'إضافة لوازم',
    bare: 'عارٍ (دون عازل)',
    customerLength: 'طول الزبون',
    inStock: 'في المخزون',
    intro: {
      materialTypes:
        'الدور يحدد وظيفة المادة: النحاس يُحتسب في حصيلة النحاس ويدخل في المرحلة الأولى، والتغليف يُقترح كلوازم للتعبئة.',
      packagingKinds:
        'فئات التعبئة (لفة، بكرة، علبة…). «بالقطع»: دون طول اسمي، يُقطع حسب الطول المطلوب.',
      paymentMethods:
        'تُقترح في الطلبيات والفواتير الأولية والفواتير والتسديدات. «نقدًا» يطبّق حق الطابع.',
      lines: 'ينجز كل خط سلسلة مرتبة من المراحل. ترتبط الآلات بخط ومرحلة.',
      wireGauges: 'الأقطار التي تنتجها آلات السحب — تحمل كل دفعة سلك مسحوب قطرها.',
      containers:
        'لكل سلة أو بكرة رقم ووزن فارغ (التارة). في الورشة نختار الوعاء ونزن: الوزن الصافي للنحاس = الإجمالي − التارة، يُحسب تلقائيًا.',
      products:
        'سلك نحاس عارٍ لكل قطر: أدخل القطر والمراحل المتّبعة. يُولَّد الرمز والتعيين تلقائيًا.',
      packaging: '«بالقطع»: كابل نهائي يُقطع بالطول الذي يطلبه الزبون (دون طول اسمي).',
      skus: 'الصنف = منتج في نوع تعبئة. يُولَّد الرمز تلقائيًا.',
      stages:
        'مسار التصنيع في المصنع. كل مرحلة تحدد وظيفتها (تحويل، مراقبة، تعبئة) وما تستهلكه وهل تُباع قيد التصنيع. المرحلة المستعملة يمكن تعطيلها فقط.',
    },
    hint: {
      computed: 'فارغ = يُحسب من قطر السلك',
      lineStages: 'انقر حسب ترتيب العملية؛ استعمل الأسهم لإعادة الترتيب.',
      routing: 'المسار: المراحل التي يمر بها هذا المنتج، بالترتيب.',
      containerCode: 'الرقم المكتوب على الوعاء (P-12، B-104…).',
      tareKg: 'وزن الوعاء فارغًا بالكغ (يوزن مرة واحدة).',
      conductorClass: '1 = صلب، 2 = مجدول، 5 = مرن',
      bare: 'فارغ لموصل عارٍ',
      colors: 'مفصولة بفواصل',
      copperKgPerKm: 'كتلة النحاس النظرية (أساس حصيلة النحاس)',
      phone: 'تستعمله أزرار «اتصال» في البوابة',
      noCap: 'فارغ = دون سقف',
      outputDiameterMm: 'مثال 3,00 لـ «سلك 3 مم». فارغ = قطر المنتج (السحب، التلدين).',
      stageKind:
        'النوع والوحدات لا تتغير بعد تسجيل إنتاج في هذه المرحلة. النحاس لا يُقترح إلا في المرحلة الأولى من المسار.',
      role: 'لا يتغير بعد إنشاء مواد من هذا النوع.',
      boughtStage:
        'نحاس مشترى جاهزًا بدل هذه المرحلة (مثلًا سلك 3 مم مشترى): المرحلة التالية تستعمله مثل الدفعات التي تنتجها. فارغ لمادة أولية.',
      cutToLength: 'لا يتغير بعد استعمال النوع في تعبئة.',
      refCode: 'فارغ = يُنشأ من التسمية. لا يتغير بعد ذلك (يُسجَّل في الوثائق).',
      stampBrackets: 'قيم استرشادية: يجب التحقق منها مع محاسبك حسب قانون المالية الساري.',
    },
    f: {
      code: 'الرمز',
      name: 'التعيين',
      label: 'التسمية',
      labelAr: 'التسمية (بالعربية)',
      isCash: 'دفع نقدي (حق الطابع)',
      cutToLength: 'بالقطع',
      containerCode: 'الرقم',
      containerKind: 'النوع',
      role: 'الدور',
      boughtStage: 'مخرَج مرحلة مشترى',
      sortOrder: 'ترتيب العرض',
      units: 'الوحدات',
      sequence: 'الترتيب في المسار',
      stageKind: 'نوع المرحلة',
      inputUnit: 'وحدة الدخول',
      outputUnit: 'وحدة الخروج',
      consumes: 'تستهلك (إضافة إلى دفعة المرحلة السابقة)',
      recordsDiameter: 'للدفعة المنتجة قطر سلك',
      outputDiameterMm: 'القطر المقترح عند الخروج (مم)',
      sellable: 'قابلة للبيع قيد التصنيع',
      icon: 'الرسم',
      active: 'نشط',
      notes: 'ملاحظات',
      description: 'الوصف',
      line: 'الخط',
      lineStages: 'المراحل المنجزة',
      stage: 'المرحلة',
      diameterMm: 'القطر (مم)',
      materialType: 'النوع',
      unit: 'الوحدة',
      lowStockThreshold: 'حد التنبيه',
      contactName: 'جهة الاتصال',
      phone: 'الهاتف',
      email: 'البريد الإلكتروني',
      city: 'المدينة',
      country: 'البلد',
      address: 'العنوان',
      conductorClass: 'فئة الموصل',
      voltageRating: 'الجهد',
      insulation: 'العازل',
      sheath: 'الغلاف',
      standard: 'المعيار',
      maxTemp: 'أقصى حرارة (°م)',
      family: 'العائلة',
      cores: 'الموصلات',
      crossSection: 'المقطع (مم²)',
      colors: 'الألوان',
      copperKgPerKm: 'النحاس (كغ/كم)',
      compoundKgPerKm: 'الخلائط (كغ/كم)',
      wireDiameterMm: 'السلك (Ø مم)',
      wiresPerConductor: 'أسلاك لكل موصل',
      outerDiameterMm: 'القطر الخارجي (مم)',
      maxResistance: 'أقصى مقاومة (أوم/كم)',
      routing: 'مسار التصنيع',
      kind: 'النوع',
      nominalLengthM: 'الطول الاسمي (م)',
      toleranceM: 'التفاوت (م)',
      tareKg: 'الوزن الفارغ (كغ)',
      supplies: 'اللوازم المستهلكة لكل وحدة',
      product: 'المنتج',
      packaging: 'نوع التعبئة',
      skuThreshold: 'حد التنبيه (وحدات)',
      portalVisible: 'ظاهر في البوابة',
      legalName: 'التسمية الاجتماعية',
      tradeName: 'الاسم التجاري',
      legalForm: 'الشكل القانوني',
      capital: 'رأس المال (دج)',
      wilaya: 'الولاية',
      postalCode: 'الرمز البريدي',
      whatsappPhone: 'واتساب',
      website: 'الموقع الإلكتروني',
      articleImposition: 'مادة الإخضاع الضريبي',
      bankName: 'البنك',
      invoiceFooter: 'تذييل الفاتورة',
      defaultVatRate: 'الرسم على القيمة المضافة الافتراضي (%)',
      copperVarianceThresholdPct: 'حد فارق حصيلة النحاس (%)',
      stampDutyEnabled: 'حق الطابع على الدفع نقدًا',
      stampDutyMin: 'أدنى طابع (دج)',
      stampDutyMax: 'أقصى طابع (دج)',
      paymentTerms: 'أجل الدفع الافتراضي (أيام)',
      daysOfCoverWindow: 'نافذة «أيام التغطية» (أيام)',
      invoicePrefix: 'بادئة الفواتير',
      creditNotePrefix: 'بادئة الإشعارات الدائنة',
      proformaPrefix: 'بادئة الفواتير الشكلية',
      deliveryNotePrefix: 'بادئة وصولات التسليم',
      salesOrderPrefix: 'بادئة الطلبيات',
      workOrderPrefix: 'بادئة أوامر التصنيع',
    },
  },
  audit: {
    intro: 'من غيّر ماذا ومتى: يُسجَّل كل إنشاء وتعديل وحذف تلقائيًا، مع القيم قبل التغيير وبعده.',
    searchPlaceholder: 'رقم الوثيقة، الرمز، الرقم التسلسلي…',
    person: 'الشخص',
    allPeople: 'كل الأشخاص',
    system: 'النظام',
    entity: 'العنصر',
    allEntities: 'كل العناصر',
    action: 'العملية',
    allActions: 'كل العمليات',
    from: 'من',
    to: 'إلى',
    reset: 'مسح عوامل التصفية',
    date: 'التاريخ',
    changes: 'التغييرات',
    created: 'أُنشئ',
    deleted: 'حُذف',
    more_one: '+حقل آخر',
    more_two: '+حقلان آخران',
    more_few: '+{{count}} حقول أخرى',
    more_many: '+{{count}} حقلًا آخر',
    more_other: '+{{count}} حقل آخر',
    field: 'الحقل',
    before: 'قبل',
    after: 'بعد',
    showAll: 'إظهار كل الحقول',
    history: 'سجل هذا العنصر',
    historyOf: 'سجل عنصر: {{entity}}',
    ip: 'عنوان IP',
    empty: 'لا توجد سجلات لعوامل التصفية هذه.',
    yes: 'نعم',
    no: 'لا',
    actions: {
      INSERT: 'إنشاء',
      UPDATE: 'تعديل',
      DELETE: 'حذف',
      LOGIN: 'تسجيل الدخول',
      LOGIN_FAILED: 'فشل تسجيل الدخول',
      LOGOUT: 'تسجيل الخروج',
      REFRESH_TOKEN_REUSE: 'جلسة مشبوهة',
    },
    groups: {
      sales: 'المبيعات',
      production: 'الإنتاج',
      stock: 'المخزون',
      catalog: 'الكتالوج',
      plant: 'المصنع',
      company: 'الشركة والمستخدمون',
    },
    entities: {
      Customer: 'زبون',
      SalesOrder: 'طلبية',
      SalesOrderLine: 'بند طلبية',
      WipReservation: 'حجز قيد التصنيع',
      DeliveryNote: 'وصل تسليم',
      DeliveryNoteItem: 'طرد وصل تسليم',
      Invoice: 'فاتورة',
      InvoiceLine: 'بند فاتورة',
      CreditNote: 'إشعار دائن',
      CreditNoteLine: 'بند إشعار دائن',
      Proforma: 'فاتورة شكلية',
      ProformaLine: 'بند فاتورة شكلية',
      Payment: 'تسديد',
      WorkOrder: 'أمر تصنيع',
      StageLog: 'تسجيل مرحلة',
      StageLogInput: 'مدخل مرحلة',
      ScrapRecord: 'هدر',
      WipLot: 'دفعة قيد التصنيع',
      LotGenealogy: 'رابط تتبّع',
      FinishedUnit: 'وحدة منتجة',
      QualityTest: 'مراقبة الجودة',
      Material: 'مادة',
      Supplier: 'مورد',
      MaterialLot: 'دفعة مادة',
      CableFamily: 'عائلة كابلات',
      CableProduct: 'منتج',
      ProductStage: 'مرحلة المسار',
      PackagingType: 'نوع تعبئة',
      PackagingSupply: 'لوازم التعبئة',
      Sku: 'صنف',
      WireGauge: 'قطر سلك',
      Stage: 'مرحلة',
      ProductionLine: 'خط إنتاج',
      LineStage: 'مرحلة خط',
      Machine: 'آلة',
      ScrapReason: 'سبب هدر',
      Company: 'الشركة',
      AppSettings: 'الإعدادات',
      StampDutyBracket: 'شريحة حق الطابع',
      User: 'مستخدم',
    },
    fields: {
      actualStart: 'البداية الفعلية',
      actualEnd: 'النهاية الفعلية',
      active: 'نشط',
      address: 'العنوان',
      amount: 'المبلغ',
      amountPaid: 'المبلغ المدفوع',
      city: 'المدينة',
      code: 'الرمز',
      colors: 'الألوان',
      confirmedAt: 'أُكّدت في',
      contactName: 'جهة الاتصال',
      copperKg: 'النحاس (كغ)',
      copperKgPerKm: 'النحاس (كغ/كم)',
      cores: 'الموصلات',
      createdBy: 'أنشأه',
      creditLimit: 'سقف الائتمان',
      crossSectionMm2: 'المقطع (مم²)',
      customer: 'الزبون',
      cutLengthM: 'طول القطعة (م)',
      deliveredAt: 'سُلّم في',
      deliveryAddress: 'عنوان التسليم',
      deliveryNote: 'وصل التسليم',
      description: 'التعيين',
      diameterMm: 'القطر (مم)',
      driverName: 'السائق',
      dueDate: 'تاريخ الاستحقاق',
      email: 'البريد الإلكتروني',
      endedAt: 'النهاية',
      family: 'العائلة',
      fullName: 'الاسم الكامل',
      grossKg: 'الوزن الإجمالي (كغ)',
      invoice: 'الفاتورة',
      issueDate: 'تاريخ الإصدار',
      kind: 'النوع',
      label: 'التسمية',
      labelAr: 'التسمية (بالعربية)',
      legalName: 'التسمية الاجتماعية',
      lengthM: 'الطول (م)',
      line: 'الخط',
      locale: 'اللغة',
      lowStockThreshold: 'حد التنبيه',
      machine: 'الآلة',
      material: 'المادة',
      method: 'طريقة الدفع',
      name: 'التعيين',
      netKg: 'الوزن الصافي (كغ)',
      notes: 'ملاحظات',
      number: 'الرقم',
      occurredAt: 'التاريخ',
      operator: 'العامل',
      order: 'الطلبية',
      orderDate: 'تاريخ الطلبية',
      outputKg: 'المخرجات (كغ)',
      outputM: 'المخرجات (م)',
      packagingType: 'نوع التعبئة',
      packedBy: 'عبّأه',
      paidAt: 'دُفع في',
      paymentMethod: 'طريقة الدفع',
      paymentStatus: 'حالة الدفع',
      paymentTermsDays: 'أجل الدفع (أيام)',
      phone: 'الهاتف',
      plannedEnd: 'النهاية المتوقعة',
      plannedStart: 'البداية المتوقعة',
      portalVisible: 'ظاهر في البوابة',
      priceUnit: 'السعر لكل',
      priority: 'الأولوية',
      producedAt: 'أُنتج في',
      product: 'المنتج',
      qcStatus: 'مراقبة الجودة',
      qty: 'الكمية',
      qtyKg: 'الكمية (كغ)',
      qtyM: 'الكمية (م)',
      qtyUnits: 'الوحدات',
      reason: 'السبب',
      receivedAt: 'استُلم في',
      receivedBy: 'استلمه',
      receivedQty: 'الكمية المستلمة',
      reference: 'المرجع',
      releasedAt: 'أُطلق في',
      remainingQty: 'المتبقي',
      requestedDeliveryDate: 'تاريخ التسليم المطلوب',
      reservedOrderLine: 'محجوز لبند',
      result: 'النتيجة',
      role: 'الدور',
      scrapKg: 'الهدر (كغ)',
      sequence: 'الترتيب',
      serial: 'الرقم التسلسلي',
      sku: 'الصنف',
      stage: 'المرحلة',
      stampDuty: 'حق الطابع',
      startedAt: 'البداية',
      status: 'الحالة',
      stockStatus: 'حالة المخزون',
      supplier: 'المورد',
      targetLengthM: 'الطول المستهدف (م)',
      targetUnits: 'الوحدات المستهدفة',
      testedAt: 'اختُبر في',
      tester: 'المراقب',
      totalHt: 'المجموع خارج الرسوم',
      totalTtc: 'المجموع متضمن الرسوم',
      totalVat: 'الرسم على القيمة المضافة',
      type: 'النوع',
      unit: 'الوحدة',
      unitCost: 'تكلفة الوحدة',
      unitPrice: 'سعر الوحدة خارج الرسوم',
      validUntil: 'صالحة حتى',
      validatedAt: 'اعتُمد في',
      validatedBy: 'اعتمده',
      vatRate: 'نسبة الرسم على القيمة المضافة',
      vehiclePlate: 'المركبة',
      voltageTestPassed: 'نجاح اختبار الجهد',
      wilaya: 'الولاية',
      workOrder: 'أمر التصنيع',
    },
  },
  users: {
    title: 'المستخدمون',
    intro: 'حسابات الموظفين ومشتري البوابة.',
    new: 'مستخدم جديد',
    fullName: 'الاسم الكامل',
    role: 'الدور',
    customer: 'الزبون (مشترٍ)',
    active: 'نشط',
    disabled: 'معطّل',
    lastLogin: 'آخر دخول',
    resetPassword: 'إعادة تعيين كلمة المرور',
    newPassword: 'كلمة المرور الجديدة',
    passwordHint: '10 أحرف على الأقل، منها حرف ورقم',
    resetEffect: 'ستُغلق كل جلسات هذا المستخدم.',
  },
  count: {
    title: 'الجرد',
    intro:
      'العدّ الفعلي للمخزون: أطلق جردًا، يمسح العمّال ويعدّون على اللوحة («الجرد»)، ثم اعتمد الفوارق.',
    new: 'جرد جديد',
    none: 'لا يوجد جرد.',
    code: 'الرقم',
    started: 'أُطلق في',
    startedBy: 'أطلقه',
    scope: 'النطاق',
    lines: 'المواد المعدودة',
    status: { OPEN: 'جارٍ', POSTED: 'معتمد', CANCELLED: 'ملغى' },
    kind: { MATERIAL_LOT: 'المواد', WIP_LOT: 'قيد التصنيع', FINISHED_UNIT: 'منتجات تامة' },
    nMaterials_zero: '{{count}} مادة',
    nMaterials_one: 'مادة واحدة',
    nMaterials_two: 'مادتان',
    nMaterials_few: '{{count}} مواد',
    nMaterials_many: '{{count}} مادة',
    nMaterials_other: '{{count}} مادة',
    nProducts_zero: '{{count}} منتج',
    nProducts_one: 'منتج واحد',
    nProducts_two: 'منتجان',
    nProducts_few: '{{count}} منتجات',
    nProducts_many: '{{count}} منتجًا',
    nProducts_other: '{{count}} منتج',
    newHint:
      'يجب مسح كل ما في المخزون ضمن النطاق: عند الاعتماد يُعتبر ما لم يُعدّ مفقودًا (إلا ما دخل بعد الإطلاق).',
    whatToCount: 'ماذا نعدّ؟',
    onlyMaterials: 'هذه المواد فقط',
    onlyProducts: 'هذه المنتجات فقط (قيد التصنيع والتامة)',
    all: '(الكل إن لم يُحدَّد شيء)',
    selected_zero: '({{count}} محدّدة)',
    selected_one: '(واحدة محدّدة)',
    selected_two: '(اثنتان محدّدتان)',
    selected_few: '({{count}} محدّدة)',
    selected_many: '({{count}} محدّدة)',
    selected_other: '({{count}} محدّدة)',
    notes: 'ملاحظة',
    start: 'إطلاق الجرد',
    startedOn: 'أُطلق في {{date}} بواسطة {{name}}',
    postedOn: 'اعتُمد في {{date}} بواسطة {{name}}',
    cancel: 'إلغاء الجرد',
    post: 'اعتماد الجرد',
    postTitle: 'اعتماد الجرد',
    postIntro: 'ستُسجَّل الفوارق في المخزون كتسويات جرد:',
    sumAdjusted_zero: '{{count}} مادة مصحَّحة',
    sumAdjusted_one: 'مادة واحدة مصحَّحة',
    sumAdjusted_two: 'مادتان مصحَّحتان',
    sumAdjusted_few: '{{count}} مواد مصحَّحة',
    sumAdjusted_many: '{{count}} مادة مصحَّحة',
    sumAdjusted_other: '{{count}} مادة مصحَّحة',
    sumMissing_zero: '{{count}} مادة غير معدودة تُخرج كمفقودة',
    sumMissing_one: 'مادة واحدة غير معدودة تُخرج كمفقودة',
    sumMissing_two: 'مادتان غير معدودتين تُخرجان كمفقودتين',
    sumMissing_few: '{{count}} مواد غير معدودة تُخرج كمفقودة',
    sumMissing_many: '{{count}} مادة غير معدودة تُخرج كمفقودة',
    sumMissing_other: '{{count}} مادة غير معدودة تُخرج كمفقودة',
    sumFound_zero: '{{count}} وحدة مُستعادة تعود إلى المخزون',
    sumFound_one: 'وحدة واحدة مُستعادة تعود إلى المخزون',
    sumFound_two: 'وحدتان مُستعادتان تعودان إلى المخزون',
    sumFound_few: '{{count}} وحدات مُستعادة تعود إلى المخزون',
    sumFound_many: '{{count}} وحدة مُستعادة تعود إلى المخزون',
    sumFound_other: '{{count}} وحدة مُستعادة تعود إلى المخزون',
    sumValue: 'فارق قيمة المواد: {{value}}',
    reservedWarn_zero: '{{count}} مادة محجوزة لطلبيات معنية: يجب إعادة الحجز.',
    reservedWarn_one: 'مادة واحدة محجوزة لطلبية معنية: يجب إعادة الحجز.',
    reservedWarn_two: 'مادتان محجوزتان لطلبيات معنيتان: يجب إعادة الحجز.',
    reservedWarn_few: '{{count}} مواد محجوزة لطلبيات معنية: يجب إعادة الحجز.',
    reservedWarn_many: '{{count}} مادة محجوزة لطلبيات معنية: يجب إعادة الحجز.',
    reservedWarn_other: '{{count}} مادة محجوزة لطلبيات معنية: يجب إعادة الحجز.',
    postIrreversible: 'لا رجعة فيه: يُصحَّح الخطأ بجرد جديد أو بتسوية.',
    postConfirm: 'اعتماد وتسجيل الفوارق',
    cancelTitle: 'إلغاء الجرد',
    cancelIntro: 'تُحفظ العدّات للذكرى، ولا يتغيّر المخزون.',
    cancelConfirm: 'إلغاء الجرد',
    openHint:
      'تُحسب الفوارق مباشرة مقارنة بالمخزون لحظة كل عدّ: يمكن أن يستمر الإنتاج أثناء الجرد. ما لم يُعدّ يُخرج كمفقود عند الاعتماد.',
    counted: 'المعدودة',
    missingIfPosted: 'غير معدودة (مفقودة)',
    anomalies_zero: '{{count}} حالة شاذة',
    anomalies_one: 'حالة شاذة واحدة',
    anomalies_two: 'حالتان شاذتان',
    anomalies_few: '{{count}} حالات شاذة',
    anomalies_many: '{{count}} حالة شاذة',
    anomalies_other: '{{count}} حالة شاذة',
    valueDiff: 'فارق قيمة المواد',
    wipDiff: 'قيد التصنيع: {{kg}}',
    differences: 'الفوارق',
    onlyDiffs: 'الفوارق فقط',
    everything: 'الكل',
    noDiffs: 'لا توجد فوارق.',
    item: 'الدفعة / الرقم التسلسلي',
    article: 'المادة',
    system: 'مخزون النظام',
    countedQty: 'المعدود',
    notCounted: 'غير معدود',
    seen: 'شوهد',
    delta: 'الفارق',
    value: 'القيمة',
    result: 'النتيجة',
    countedBy: 'عدّه',
    posted: 'سُجّل في المخزون',
    cancelledLines: 'العدّات (جرد ملغى)',
    outcome: {
      MATCH: 'مطابق',
      ADJUSTED: 'فارق',
      MISSING: 'مفقود',
      FOUND: 'مُستعاد',
      ANOMALY: 'حالة شاذة',
    },
    capped: 'محدود: انخفض المخزون منذ العدّ',
    reservedFor: 'محجوز: {{order}}',
    anomalyHint: 'يعتبره النظام خارج المخزون (مُسلَّم، مُهدَر…): للتحقق، لم يُسجَّل شيء.',
  },
  opc: {
    title: 'الجرد',
    stepPick: 'الجرد',
    stepScan: 'العدّ',
    none: 'لا يوجد جرد جارٍ. يطلقه المسؤول من المكتب (المخزون › الجرد).',
    startedBy: 'أُطلق في {{date}} بواسطة {{name}}',
    linesSoFar_zero: '{{count}} مادة معدودة',
    linesSoFar_one: 'مادة واحدة معدودة',
    linesSoFar_two: 'مادتان معدودتان',
    linesSoFar_few: '{{count}} مواد معدودة',
    linesSoFar_many: '{{count}} مادة معدودة',
    linesSoFar_other: '{{count}} مادة معدودة',
    scan: 'امسح / أدخل رمزًا (دفعة أو رقم تسلسلي)',
    scanHint: 'امسح الملصق. اللفة أو البكرة تُعدّ بمسحها؛ للدفعة أدخل الكمية الموجودة.',
    outOfScope: '{{code}} ({{label}}) ليس ضمن هذا الجرد',
    unitCounted: 'تم عدّ {{code}}',
    lotCounted: '{{code}}: سُجّل {{qty}}',
    countedSoFar_zero: '{{count}} مادة معدودة',
    countedSoFar_one: 'مادة واحدة معدودة',
    countedSoFar_two: 'مادتان معدودتان',
    countedSoFar_few: '{{count}} مواد معدودة',
    countedSoFar_many: '{{count}} مادة معدودة',
    countedSoFar_other: '{{count}} مادة معدودة',
    nothingYet: 'لم يُعدّ شيء بعد.',
    remove: 'إزالة {{code}}',
    alreadyCounted: 'عُدّ سابقًا: {{qty}}. الإدخال الجديد يحلّ محلّ القديم.',
    qty: 'الكمية الموجودة',
    qtyKg: 'الوزن الموجود',
    wholePieces: 'عدد القطع الكاملة',
    lengthOptional: 'الطول (اختياري)',
    lengthHint: 'وإلا يُستنتج من الوزن.',
    note: 'ملاحظة',
    save: 'تسجيل العدّ',
    qtyRequired: 'أدخل الكمية الموجودة (0 إن كانت الدفعة فارغة).',
    closed: 'الجرد {{code}} مُغلق.',
  },
  inv: {
    nav: { stock: 'حالة المخزون' },
    materials: 'المواد الأولية واللوازم',
    wip: 'المنتجات قيد التصنيع (سلك 3 مم، مسحوب، ملدّن…)',
    wipIntro: 'دفعات أنتجتها الورشة ولم تُستهلك بعد، حسب المرحلة وقطر السلك.',
    noWip: 'لا توجد منتجات قيد التصنيع في المخزون.',
    byWeight: 'المخزون النهائي بالوزن',
    byWeightIntro:
      'إنتاج أوامر التصنيع «للمخزون» المنتهية (مثلًا السلك الملدّن)، حسب المنتج والقطر. يُباع بالكيلوغرام؛ ويمكن لمرحلة لاحقة استعماله.',
    noByWeight: 'لا يوجد مخزون نهائي بالوزن.',
    container: 'الوعاء',
    fusion: {
      title: 'الصهر',
      summary: 'خردة مذابة {{melted}} كغ ← نحاس محصَّل {{obtained}} كغ · خبث {{slag}} كغ.',
      obtained: 'النحاس المحصَّل الصحيح (كغ)',
      used: 'استُعملت هذه الدفعة: صحّح بتعديل جرد أدناه.',
    },
    product: 'المنتج',
    from: 'المرحلة',
    stage: 'المرحلة',
    diameter: 'القطر',
    reserved: 'محجوز',
    onHold: 'موقوف',
    copper: 'النحاس',
    madeFor: 'مصنوع لـ',
    threshold: 'الحد الأدنى',
    toPrice: 'السعر ناقص',
    workOrder: 'أمر التصنيع',
    producedAt: 'الإنتاج',
    status: 'الحالة',
    low: 'مخزون منخفض',
    onHand: 'في المخزون',
    openLots: 'دفعات مفتوحة',
    lotsFifo: 'دفعات (ترتيب FIFO)',
    includeDepleted: 'إدراج الدفعات المستنفدة',
    receivedBy: 'استلمها',
    ref: 'المرجع',
    by: 'بواسطة',
    value: 'القيمة',
    lot: 'الدفعة',
    receivedAt: 'الاستلام',
    received: 'المستلم',
    remaining: 'المتبقي',
    movements: 'الحركات',
    date: 'التاريخ',
    type: 'النوع',
    qty: 'الكمية',
    adjust: 'تسوية الجرد',
    delta: 'الفرق',
    reason: 'السبب',
  },
  op: {
    backOffice: 'العودة إلى المكتب',
    pad: {
      label: 'لوحة الأرقام',
      comma: 'فاصلة',
      back: 'حذف آخر رقم',
      clear: 'مسح الكل',
      next: 'الحقل التالي',
      step: 'الخطوة {{step}} من {{total}}',
      continue: 'التالي',
    },
    find: {
      label: 'امسح أو اكتب دفعة أو حاوية',
      placeholder: 'امسح أو اكتب: دفعة، سلة، بكرة',
      notFound: '{{code}}: غير موجود',
      empty: 'فارغة',
    },
    hello: 'مرحبًا، {{name}}',
    whatNow: 'ماذا تفعل الآن؟',
    shift: {
      MORNING: 'الفريق الصباحي',
      AFTERNOON: 'فريق المساء',
      NIGHT: 'الفريق الليلي',
    },
    waiting: {
      lowMaterials_zero: '{{count}} مادة تحت الحد',
      lowMaterials_one: 'مادة واحدة تحت الحد',
      lowMaterials_two: 'مادتان تحت الحد',
      lowMaterials_few: '{{count}} مواد تحت الحد',
      lowMaterials_many: '{{count}} مادة تحت الحد',
      lowMaterials_other: '{{count}} مادة تحت الحد',
      openWos_zero: '{{count}} أمر تصنيع جارٍ',
      openWos_one: 'أمر تصنيع جارٍ',
      openWos_two: 'أمرا تصنيع جاريان',
      openWos_few: '{{count}} أوامر تصنيع جارية',
      openWos_many: '{{count}} أمر تصنيع جارٍ',
      openWos_other: '{{count}} أمر تصنيع جارٍ',
      noWo: 'لا يوجد أمر تصنيع مطلق',
      pendingUnits_zero: '{{count}} وحدة للمراقبة',
      pendingUnits_one: 'وحدة واحدة للمراقبة',
      pendingUnits_two: 'وحدتان للمراقبة',
      pendingUnits_few: '{{count}} وحدات للمراقبة',
      pendingUnits_many: '{{count}} وحدة للمراقبة',
      pendingUnits_other: '{{count}} وحدة للمراقبة',
      heldLots_zero: '{{count}} دفعة موقوفة',
      heldLots_one: 'دفعة واحدة موقوفة',
      heldLots_two: 'دفعتان موقوفتان',
      heldLots_few: '{{count}} دفعات موقوفة',
      heldLots_many: '{{count}} دفعة موقوفة',
      heldLots_other: '{{count}} دفعة موقوفة',
      openCounts_zero: '{{count}} جرد جارٍ',
      openCounts_one: 'جرد جارٍ',
      openCounts_two: 'جردان جاريان',
      openCounts_few: '{{count}} عمليات جرد جارية',
      openCounts_many: '{{count}} عملية جرد جارية',
      openCounts_other: '{{count}} عملية جرد جارية',
    },
    steps: {
      material: 'المادة',
      quantity: 'الكمية',
      label: 'الملصق',
      workOrder: 'أمر التصنيع',
      stage: 'المرحلة',
      diameter: 'المنتج',
      entry: 'الإدخال',
      packing: 'التعبئة',
    },
    toRecord: 'للتسجيل',
    outputWeight: 'الوزن المنتج',
    inputsTotal: 'المدخلات',
    lotsUsed: 'الدفعات المستعملة',
    output: 'الإنتاج',
    fifo: 'الأقدم أولًا (FIFO)',
    gauge: {
      output: 'المنتَج',
      scrap: 'الهدر',
      gap: 'الفارق',
      over: 'الفائض',
      offcut: 'البقية',
      left: 'المتبقي',
    },
    tilesDesc: {
      wire3mm: 'خردة النحاس المذابة ← سلك 3 مم',
      receive: 'خردة النحاس، PVC، التغليف',
      produce: 'السحب، التلدين… انطلاقًا من سلك 3 مم',
      pack: 'لفات، بكرات، قصّ',
      qc: 'الاختبارات واعتماد الوحدات',
      count: 'عدّ المخزون بالمسح',
    },
    soon: 'قريبًا',
    tiles: {
      wire3mm: 'الصهر',
      receive: 'استلام المواد',
      produce: 'الإنتاج',
      pack: 'التعبئة',
      qc: 'مراقبة الجودة',
      count: 'الجرد',
    },
    stocks: {
      raw: 'خردة النحاس في المخزون',
      wire3mm: 'سلك 3 مم في المخزون',
    },
    wire3mm: {
      steps: { baskets: 'السلال', weigh: 'الوزن', balance: 'الحصيلة', done: 'انتهى' },
      pickTitle: 'ما السلال الخارجة من الفرن؟',
      noBasket: 'دون سلة',
      noBasketHint: 'وزن واحد: النحاس المحصَّل',
      weighTitle: 'وزن السلال',
      weighSub: 'اكتب الوزن القائم لكل سلة؛ السلة التي تُترك فارغة لا تُسجَّل',
      weighOne: 'وزن النحاس المحصَّل',
      weighOneSub: 'اكتب الوزن المقروء على الميزان',
      basket: 'السلة {{code}}',
      tareNet: 'الفارغ {{tare}} كغ · الصافي {{net}}',
      weighed: 'السلال الموزونة',
      grossTare: '{{gross}} − الفارغ {{tare}}',
      producedTotal: 'سلك 3 مم المُنتَج',
      balanceTitle: 'حصيلة الصهر',
      balanceSub: 'اكتب الأوزان المقروءة على الميزان',
      usedShort: 'الخردة المصهورة',
      usedSub: 'موزونة قبل التحميل (فارغ = النحاس المحصَّل)',
      slagSub: 'الشوائب المزالة من الفرن',
      recoveredShort: 'الخردة المسترجعة',
      recoveredSub: 'تعود إلى مخزون الخردة',
      legend3mm: '3 مم',
      legendRecovered: 'مسترجع',
      legendSlag: 'خبث',
      gapChip: 'الفرق {{kg}} كغ · {{pct}} %',
      overChip: '{{kg}} كغ زائدة: تحقق من الأوزان',
      barAria:
        '{{used}} كغ مصهورة: سلك 3 مم {{out}} كغ، مسترجع {{rec}} كغ، خبث {{slag}} كغ، فرق {{gap}} كغ',
      yieldLine: 'مردود الصهر {{pct}} %',
      stockLine: 'الخردة في المخزون: {{before}} ← {{after}} كغ',
      validate: 'المصادقة على الصهر',
      finish: 'اعتماد',
      toBalance: 'الحصيلة',
      baskets: 'السلال المملوءة',
      basketsHint:
        'اختياري: المس كل سلة مُلئت عند خروج الفرن (كل سلة موزونة تصبح دفعة سلك 3 مم)، أو تابع دون سلة بوزن واحد. الحصيلة (الخردة المصهورة، الخبث، المسترجع) اختيارية أيضًا: «اعتماد» مباشرة بعد الوزن.',
      intro:
        'زن ما يدخل الفرن، ثم النحاس المحصَّل والخبث. تخرج الخردة من مخزون النحاس ويدخل النحاس المحصَّل إلى مخزون 3 مم.',
      produced: 'النحاس المحصَّل',
      used: 'خردة النحاس المذابة',
      usedHint: 'وزن الخردة الموضوعة في الفرن (فارغ = نفس وزن النحاس المحصَّل).',
      recovered: 'خردة مسترجعة (تعود إلى المخزون)',
      recoveredHint:
        'سيلان، قطع، أخطاء صهر: يعود هذا الوزن إلى مخزون الخردة لإعادة صهره (ليس ضياعًا).',
      slag: 'الخبث',
      slagHint: 'إذا وزنتموه: يُحسب ضياعًا مصرَّحًا به، لا فارقًا.',
      yield: 'مردود الصهر',
      loss: 'فارق غير مصرَّح به',
      after: 'بعد الحفظ',
      material: 'أي سلك 3 مم؟',
      notEnough: 'خردة النحاس في المخزون غير كافية: {{kg}} كغ متاحة.',
      noMaterial: 'لا توجد مادة «سلك 3 مم». اطلب من المسؤول إنشاءها (الإعدادات › المواد).',
      confirm: 'حفظ',
      done: 'تم تسجيل الصهر',
      another: 'إدخال آخر',
    },
    receive: {
      step1: 'ما المادة التي تستلمها؟',
      step2: 'الاستلام',
      inStock: 'في المخزون',
      qty: 'الكمية المستلمة',
      supplier: 'المورد',
      certRef: 'رقم الشهادة',
      supplierLot: 'دفعة المورد',
      unitCost: 'سعر الوحدة دون رسوم',
      unitCostHint: 'للكيلوغرام، دون رسوم. يراه المالك وحده؛ فارغ = يُكمَل لاحقًا.',
      value: 'القيمة دون رسوم',
      costHint: 'معبأ مسبقًا بآخر سعر شراء.',
      confirm: 'تأكيد الاستلام',
      done: 'تم إنشاء الدفعة',
      copies: 'الملصقات:',
      print: 'طباعة الملصق',
      another: 'استلام جديد',
      finish: 'انتهى',
    },
  },
  qcStatus: { PENDING: 'في الانتظار', PASS: 'مطابق', FAIL: 'غير مطابق' },
  opp: {
    sub: {
      reservedTo: 'محجوزة لـ',
      oneByOne: 'اختيار السلال / البكرات واحدة واحدة',
      baskets_zero: '{{count}} سلة',
      baskets_one: 'سلة واحدة',
      baskets_two: 'سلتان',
      baskets_few: '{{count}} سلال',
      baskets_many: '{{count}} سلة',
      baskets_other: '{{count}} سلة',
      bobbins_zero: '{{count}} بكرة',
      bobbins_one: 'بكرة واحدة',
      bobbins_two: 'بكرتان',
      bobbins_few: '{{count}} بكرات',
      bobbins_many: '{{count}} بكرة',
      bobbins_other: '{{count}} بكرة',
      containers_zero: '{{count}} حاوية',
      containers_one: 'حاوية واحدة',
      containers_two: 'حاويتان',
      containers_few: '{{count}} حاويات',
      containers_many: '{{count}} حاوية',
      containers_other: '{{count}} حاوية',
      loose_zero: '{{count}} دفعة دون حاوية',
      loose_one: 'دفعة واحدة دون حاوية',
      loose_two: 'دفعتان دون حاوية',
      loose_few: '{{count}} دفعات دون حاوية',
      loose_many: '{{count}} دفعة دون حاوية',
      loose_other: '{{count}} دفعة دون حاوية',
      freeKg: '{{kg}} كغ متاحة',
      stage: 'المرحلة',
      inputs: 'المدخل',
      container: 'البكرة',
      weigh: 'الوزن',
      scrap: 'الهدر',
      inputsHint:
        'المس «استعمال» لاختيار دفعة المدخل (أو امسح سلتها). دون اختيار، تؤخذ الدفعات الأقدم.',
      autoInputs: 'الأقدم (تلقائيًا)',
      output: 'بكرة الخروج',
      noContainer: 'دون حاوية',
      tareOf: 'فارغ {{code}}',
      net: 'الوزن الصافي',
      over: 'أثقل من المدخل ({{kg}} كغ): تحقق من الميزان',
      left: 'المدخل: يبقى {{kg}} كغ للإخراج أو للتصريح كهدر',
      validateWeigh: 'المصادقة على الوزن',
      scrapHint: 'اتركه فارغًا إن لم يكن هناك هدر',
    },
    freeBobbin: 'بكرة بلا رقم',
    freeBobbinHint: 'إدخال الوزن الفارغ',
    freeTare: 'الوزن الفارغ للبكرة',
    pickWo: 'اختر أمر التصنيع',
    nextStage: 'المرحلة التالية',
    stageDone: 'منجزة',
    qcPick: 'حدد الوحدات المراد مراقبتها',
    qcUnitsWord_zero: 'وحدة',
    qcUnitsWord_one: 'وحدة',
    qcUnitsWord_two: 'وحدتان',
    qcUnitsWord_few: 'وحدات',
    qcUnitsWord_many: 'وحدة',
    qcUnitsWord_other: 'وحدة',
    noWo: 'لا يوجد أمر تصنيع مطلق. اطلب من المسؤول إطلاق أمر.',
    pickStage: 'ما المرحلة التي تنجزها؟',
    next: 'التالية',
    machine: 'الآلة',
    scan: 'امسح / أدخل رمز الدفعة',
    scanUnknown: 'لا يمكن استعمال هذه الدفعة في هذه المرحلة',
    wipInputs: 'دفعات قيد التصنيع (المرحلة السابقة)',
    materialInputs: 'المواد',
    container: 'الوزن في وعاء',
    containerHint:
      'اختر السلة أو البكرة (أو امسح رقمها)، ثم أدخل الوزن الإجمالي: يُحسب الصافي (الإجمالي − التارة) تلقائيًا ويؤخذ الداخل من المخزون، الأقدم أولًا.',
    containerReuse: 'تحتوي المدخل {{code}}: تُفرَغ ثم يُعاد استعمالها',
    containerFull: 'ممتلئ: {{code}}',
    tare: 'التارة {{kg}} كغ',
    gross: 'الوزن الإجمالي — {{code}}',
    net: 'الوزن الصافي (بعد خصم التارة {{kg}} كغ)',
    submitNext: 'حفظ ووزن التالي',
    scrapGoesTo: 'ماذا يصبح هذا الهدر؟',
    recycle: 'عودة إلى الخردة (لإعادة الصهر)',
    lost: 'ضائع',
    continueTo: 'المتابعة: {{stage}}',
    pickStep: 'أي مرحلة تنجز؟',
    pickDiameter: 'ماذا تصنع؟',
    plannedOf: 'تسجيل لأمر تصنيع مخطط',
    workshop: 'الورشة',
    readyContainers_zero: '{{kg}} كغ جاهزة في {{count}} وعاء ({{step}})',
    readyContainers_one: '{{kg}} كغ جاهزة في وعاء واحد ({{step}})',
    readyContainers_two: '{{kg}} كغ جاهزة في وعاءين ({{step}})',
    readyContainers_few: '{{kg}} كغ جاهزة في {{count}} أوعية ({{step}})',
    readyContainers_many: '{{kg}} كغ جاهزة في {{count}} وعاءً ({{step}})',
    readyContainers_other: '{{kg}} كغ جاهزة في {{count}} وعاء ({{step}})',
    ready: '{{kg}} كغ جاهزة ({{step}})',
    rawMaterial: 'مادة',
    nothingWaiting: 'لا شيء في الانتظار لهذا المنتج',
    noProductForStep: 'لا يمر أي منتج بهذه المرحلة (الإعدادات › المنتجات).',
    savedNext: 'تم تسجيل الدفعة {{code}}. اختر الوعاء التالي.',
    boughtInputs: 'سلك في المخزون',
    bought: 'مخزون',
    madeFor: 'مصنوع لـ',
    otherWo: 'أمر آخر',
    use: 'استعمال',
    used: 'مستعمَل',
    leftover: {
      title: '{{kg}} كغ مأخوذة زيادة؟',
      body: 'أخذتم {{taken}} كغ كمدخل، لكن الوزن والهدر لا يفسّران سوى {{out}} كغ.',
      keep: 'إبقاء {{kg}} كغ في المخزون (في سلالها)',
      scrap: 'إنها خسارة: التصريح بـ {{kg}} كغ كهدر',
    },
    unuse: 'إزالة',
    qtyKg: 'الكمية (كغ)',
    outputKg: 'الوزن المنتج (كغ)',
    outputM: 'الطول المنتج (م)',
    diameter: 'قطر السلك',
    scrapKg: 'الهدر (كغ)',
    scrapReason: 'سبب الهدر',
    notes: 'ملاحظة',
    submit: 'تسجيل المرحلة',
    testingInfo:
      'مرحلة الاختبارات: تسجل المرور على منصة الاختبار. تُدخل القياسات في «مراقبة الجودة».',
    doneTitle: 'تم تسجيل المرحلة',
    newLot: 'دفعة جديدة قيد التصنيع',
    printLabel: 'طباعة ملصق الدفعة',
    another: 'إدخال جديد',
    noWip: 'لا توجد دفعة متاحة في المرحلة السابقة.',
    packTitle: 'التعبئة',
    pickSource: 'الدفعة المراد تعبئتها',
    packaging: 'نوع التعبئة',
    units: 'الوحدات',
    addUnit: 'إضافة وحدة',
    pieceLength: 'الطول (م)',
    pieceWeight: 'الوزن الصافي (كغ)',
    packBy: 'التعبئة',
    byWeight: 'حسب الوزن (وزن)',
    byLength: 'حسب الطول',
    totalKg: 'المجموع: {{m}} كغ من {{avail}} كغ متاحة',
    offcut: 'البقية (م) للهدر',
    offcutKg: 'البقية (كغ) للهدر',
    totalM: 'المجموع: {{m}} م من {{avail}} م متاحة',
    packSubmit: 'إنشاء الوحدات',
    packDone: 'تم إنشاء الوحدات',
    printUnits: 'طباعة الملصقات',
    qcTitle: 'مراقبة الجودة',
    qcUnits: 'الوحدات التامة',
    qcWip: 'دفعات قيد التصنيع',
    wipAvailable: 'المتاحة',
    wipHeld: 'الموقوفة',
    wipNone: 'لا توجد دفعات.',
    blocked: 'موقوف',
    pickLot: 'اختر الدفعة المراد مراقبتها (مسح أو قائمة).',
    lastTest: 'آخر مراقبة: {{result}} في {{date}}',
    untested: 'لم تُراقَب بعد',
    wireDiameter: 'قطر السلك (مم)',
    qcNotes: 'ملاحظة (السبب في حال عدم المطابقة)',
    wipFailHint:
      'عدم المطابقة يوقف الدفعة: لا يمكن استهلاكها ولا تسليمها، فقط إهدارها. مراقبة مطابقة ترفع الإيقاف.',
    wipBlocked: '{{code}}: غير مطابق — الدفعة موقوفة (لا استهلاك ولا تسليم)',
    wipReleased: '{{code}}: مطابق — الدفعة متاحة',
    qcPending: 'وحدات في انتظار المراقبة',
    qcNone: 'لا توجد وحدات في الانتظار.',
    qcMore: 'عُرضت {{shown}} وحدة من أصل {{total}} (الأقدم): افحصها وستظهر البقية.',
    selectAll: 'تحديد الكل',
    resistance: 'مقاومة الموصل (أوم/كم)',
    maxR: 'الحد الأقصى {{max}}',
    insulation: 'مقاومة العزل (ميغا أوم·كم)',
    voltage: 'اختبار الجهد (ك.ف)',
    voltageOk: 'تحمّل الجهد',
    diameterOut: 'القطر الخارجي (مم)',
    pass: 'مطابق',
    fail: 'غير مطابق',
    selected: '{{count}} محددة',
    suggestFail: 'المقاومة أعلى من الحد الأقصى: يُنصح بعدم المطابقة',
  },
  setup: {
    title: 'التثبيت الأول',
    intro: 'أنشئ حساب المالك. يمكنه بعد ذلك إنشاء المستخدمين الآخرين وإعداد المصنع.',
    submit: 'إنشاء حساب المالك',
  },
  login: {
    subtitle: 'سجّل الدخول للوصول إلى تسيير المصنع.',
    headline: 'من خردة النحاس إلى السلك المسلَّم، كل كيلوغرام متتبَّع.',
    points: {
      trace: 'تتبّع كل دفعة من خردة النحاس حتى التسليم',
      copper: 'حصيلة النحاس بشكل مستمر مع تنبيه تلقائي عند الفوارق',
      invoice: 'فوترة مطابقة، ترقيم دون ثغرات، حق الطابع',
    },
  },
  auth: {
    title: 'تسجيل الدخول',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    submit: 'دخول',
    submitting: 'جارٍ الدخول…',
    logout: 'تسجيل الخروج',
    invalid: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
    forbidden: 'ليس لحسابك صلاحية الوصول إلى هذا التطبيق.',
    genericError: 'حدث خطأ. حاول مرة أخرى.',
  },
  help: {
    back: 'رجوع',
    scope: 'الأقسام المعروضة',
    all: 'الدليل كاملًا',
    mine: 'لدوري',
    contents: 'الفهرس',
  },
  flow: {
    stockTitle: 'المخزون حسب مرحلة الإنتاج',
    saleTitle: 'البيع — من أين تُؤخذ البضاعة',
    live: 'تحديث مستمر',
    raw: 'خردة النحاس',
    rawIn: 'الخردة: {{kg}} كغ',
    bought: 'سلك في المخزون (مصنوع أو مشترى)',
    byWeight: 'بالوزن (أوامر للمخزون منتهية)',
    byWeightIn: 'بالوزن: {{kg}} كغ',
    finished: 'المخزون النهائي',
    customer: 'الزبون',
    lots_zero: '{{count}} دفعة',
    lots_one: 'دفعة واحدة',
    lots_two: 'دفعتان',
    lots_few: '{{count}} دفعات',
    lots_many: '{{count}} دفعة',
    lots_other: '{{count}} دفعة',
    units_zero: '{{count}} وحدة',
    units_one: 'وحدة واحدة',
    units_two: 'وحدتان',
    units_few: '{{count}} وحدات',
    units_many: '{{count}} وحدة',
    units_other: '{{count}} وحدة',
    running_zero: 'لا يوجد أمر تصنيع جارٍ',
    running_one: 'أمر تصنيع جارٍ',
    running_two: 'أمرا تصنيع جاريان',
    running_few: '{{count}} أوامر تصنيع جارية',
    running_many: '{{count}} أمر تصنيع جارٍ',
    running_other: '{{count}} أمر تصنيع جارٍ',
    empty: 'لا يوجد مخزون',
    low: 'منخفض',
    sellHere: 'بيع',
    available: 'متاح للبيع',
    length: 'الطول',
    reserved: 'محجوز',
    reservedPending: 'محجوزة / في انتظار المراقبة',
    byDiameter: 'حسب القطر:',
    clickHint: 'انقر على مرحلة لعرض التفاصيل.',
    hint: {
      PACKAGED: 'لفائف وبكرات من المخزون النهائي (مطابقة للمراقبة وغير محجوزة).',
      CUT_LENGTH: 'كابل نهائي مقطوع بالطول الذي يطلبه الزبون.',
      IN_PROCESS: 'منتج يؤخذ أثناء التصنيع: سلك مسحوب حسب القطر، موصلات، موصلات معزولة…',
    },
  },
};
